import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { getFormData } from '../utils/api';
import DocumentHistoryButton from '../components/DocumentHistoryButton';

import toast from 'react-hot-toast';
import {
  Plus, Search, Eye, Download, DollarSign, Calculator,
  FileText, ChevronLeft, ChevronRight, AlertCircle, Trash2, Link2
} from 'lucide-react';
import { generatePDF } from '../utils/pdfGenerator';
import { PaymentStatusBadge, calculatePaymentStatus, normalizePaymentData } from '../utils/paymentUtils';
import PaymentModal from '../components/PaymentModal';

import CancellationModal from '../components/CancellationModal';
import { paymentSync } from '../utils/paymentSync';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import LastUpdatedIndicator from '../components/LastUpdatedIndicator';
import { calculateTotalGSTBreakdown } from '../utils/gstUtils';
import { GST_STATE_CODES, getStateNameFromGST } from '../utils/gstUtils';
import GSTBreakdown, { GSTSummary } from '../components/GSTBreakdown';
import { useAuth } from '../context/AuthContext';
import InvoiceNumberField from '../components/InvoiceNumberField';
import TermsAndConditionsInput from '../components/TermsAndConditionsInput';
import FilterDropdown from '../components/FilterDropdown';
import ValidatedSelect from '../components/ValidatedSelect';
import AdvancedFilters from '../components/AdvancedFilters';
import AppModal, { modalTable, modalButton } from '../components/AppModal';
import { recalcItemsAmounts } from '../utils/itemAmounts';

// Utility function for currency formatting
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₹0.00';
  }
  return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper: compute qty for a woodwork item (Nos × L × B; if L or B is 0, qty = Nos)
const computeWoodWorkQty = (nos, length, breadth) => {
  const n = parseFloat(nos) || 0;
  const l = parseFloat(length) || 0;
  const b = parseFloat(breadth) || 0;
  if (l === 0 || b === 0) return n; // Tandon Boxes / hardware items
  return parseFloat((n * l * b).toFixed(4));
};

// Invoice Number Display Component
const InvoiceNumberDisplay = ({ closedOrderId, invoiceId, companySettings }) => {
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoiceNumber = async () => {
      try {
        const response = await api.get(`/sales/closed-orders/${closedOrderId}/invoice-number`);
        setInvoiceNumber(response.data.invoice_number);
      } catch (error) {
        console.error('Failed to fetch invoice number:', error);
      } finally {
        setLoading(false);
      }
    };

    if (closedOrderId) {
      fetchInvoiceNumber();
    }
  }, [closedOrderId]);

  const handleInvoiceDownload = async () => {
    try {
      // Fetch the invoice data and generate PDF
      const response = await api.get(`/sales/invoices/${invoiceId}`);
      const invoiceData = response.data.invoice || response.data;
      await generatePDF(invoiceData, 'invoice', companySettings);
    } catch (error) {
      console.error('Failed to download invoice PDF:', error);
      toast.error('Failed to download invoice PDF');
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400">Loading...</div>;
  }

  if (!invoiceNumber) {
    return null;
  }

  return (
    <button
      onClick={handleInvoiceDownload}
      className="text-xs text-[#0A5F38] hover:text-blue-800 hover:underline font-medium"
      title="Download Invoice PDF"
    >
      {invoiceNumber}
    </button>
  );
};

// State options for dropdown
const stateOptions = Object.entries(GST_STATE_CODES).map(([code, name]) => ({
  value: name,
  label: name
})).sort((a, b) => a.label.localeCompare(b.label));

// Helper for GST field changes
const handleGSTFieldChange = (gstValue, setGst, setState) => {
  setGst(gstValue);
  if (gstValue.length >= 2) {
    const stateName = getStateNameFromGST(gstValue);
    if (stateName) {
      setState(stateName);
    }
  }
};

const AdminClosedOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // State variables
  const [closedOrders, setClosedOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  
  // Advanced Filters State
  const [filters, setFilters] = useState({
    dateRange: 'all',
    status: 'all',
    paymentStatus: 'all',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    isWoodWork: 'all',
    salesperson_id: 'all'
  });
  
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [salespersons, setSalespersons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef(searchTerm);
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showEstimateDetailModal, setShowEstimateDetailModal] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [estimateSearchTerm, setEstimateSearchTerm] = useState('');
  const [paymentData, setPaymentData] = useState({
    paymentType: 'cash',
    amount: '',
    notes: ''
  });

  const [closedOrderData, setClosedOrderData] = useState({
    orderDescription: '',
    paymentType: 'cash',
    amountReceived: 0,
    paymentStatus: 'pending',
    termsAndConditions: ''
  });
  const [editingProducts, setEditingProducts] = useState([]);
  const [showEditProductsModal, setShowEditProductsModal] = useState(false);
  const [editingApplyDiscount, setEditingApplyDiscount] = useState(false);
  const [isWoodWorkMode, setIsWoodWorkMode] = useState(false);
  const [editModalContext, setEditModalContext] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [showEditClosedOrderModal, setShowEditClosedOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editSummaryMode, setEditSummaryMode] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  // Convert to Invoice states
  const [showConvertToInvoiceModal, setShowConvertToInvoiceModal] = useState(false);
  const [includeTermsClosedOrderModal, setIncludeTermsClosedOrderModal] = useState(false);
  const [includeTermsClosedOrder, setIncludeTermsClosedOrder] = useState(false);
  const [convertingClosedOrder, setConvertingClosedOrder] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const [conversionPaymentData, setConversionPaymentData] = useState({
    payment_type: 'cash',
    amount_received: 0,
    payment_status: 'pending',
    gst_number: '',
    state: '',
    description: '',
    termsAndConditions: '',
    dispatched_from: '',
    delivered_to: ''
  });
  const [conversionEditingCustomer, setConversionEditingCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    state: ''
  });

  // Cancellation Modal states
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);

  // Payment Status Update Modal states (for cancelled orders)
  const [showPaymentStatusUpdateModal, setShowPaymentStatusUpdateModal] = useState(false);
  const [paymentStatusUpdateLoading, setPaymentStatusUpdateLoading] = useState(false);
  const [paymentStatusUpdate, setPaymentStatusUpdate] = useState('');

  // Delete Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);

  // Stable ref — tracks current page without causing effect re-runs
  const currentPageRef = useRef(1);

  // Use modal scroll lock for various modals (AFTER all state variables are declared)
  useModalScrollLock(showViewModal || showConvertModal || showPaymentModal || showEstimateDetailModal || showConvertToInvoiceModal || showCancellationModal || showPaymentStatusUpdateModal);

  // 1. Core Data Fetching Effect
  useEffect(() => {
    fetchClosedOrders(1);
  }, []);

  // Handle ?view= deep link
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    if (viewParam) {
      const fetchAndOpen = async () => {
        try {
          // Search for this specific order
          const res = await api.get(`/sales/closed-orders?search=${viewParam}`);
          if (res.data.closedOrders && res.data.closedOrders.length > 0) {
            // Find exact match just in case search returned multiple
            const exactMatch = res.data.closedOrders.find(o => String(o.closed_order_number) === String(viewParam));
            if (exactMatch) {
              setTimeout(() => handleViewOrder(exactMatch), 500); // slight delay to ensure UI is ready
            } else {
              setTimeout(() => handleViewOrder(res.data.closedOrders[0]), 500);
            }
          }
        } catch (e) {
          console.error('Failed to deep link view order:', e);
        }
      };
      fetchAndOpen();
    }
  }, [location.search]);

  // 1b. Auto-apply filters (debounced) — watches primitives only
  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) { filterMounted.current = true; return; }
    const t = setTimeout(() => fetchClosedOrders(1), 500);
    return () => clearTimeout(t);
  }, [filters.status, filters.paymentStatus, filters.startDate, filters.endDate, filters.minAmount, filters.maxAmount, filters.isWoodWork, filters.salesperson_id, filters.dateRange]);

  // 2. Debounced Search Effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClosedOrders(1); // Reset to page 1 on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 3. Auto-refresh Interval Management (30s) — uses ref so fetch-driven state changes don't recreate the interval
  useEffect(() => {
    const AUTO_REFRESH_INTERVAL = 30000;
    const isAnyModalOpen = showViewModal || showConvertToInvoiceModal || showPaymentModal ||
      showCancellationModal || showEditClosedOrderModal || showEditProductsModal;

    if (!isAnyModalOpen) {
      const interval = setInterval(() => {
        console.log('🔄 Auto-refreshing closed orders (preserving page and search)...');
        fetchClosedOrders(currentPageRef.current);
      }, AUTO_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [showViewModal, showConvertToInvoiceModal, showPaymentModal, showCancellationModal,
    showEditClosedOrderModal, showEditProductsModal]);

  // 4. Initial setup
  useEffect(() => {
    fetchCompanySettings();
    fetchSalespersons();
  }, []);
  
  const fetchSalespersons = async () => {
    try {
      const res = await api.get('/sales/salespersons');
      setSalespersons(res.data);
    } catch (e) {
      console.error('Failed to load salespersons', e);
    }
  };



  useEffect(() => {
    filterOrders();
  }, [closedOrders, searchTerm, sortConfig]);

  // Functions
  const fetchClosedOrders = async (page = 1) => {
    try {
      setLoading(true);
      const currentFilters = filtersRef.current || filters;
      const currentSearch = (searchTermRef.current !== undefined ? searchTermRef.current : searchTerm) || '';
      const params = {
        page,
        limit: 10,
        include_converted: 'true',
        status: currentFilters.status !== 'all' ? currentFilters.status : undefined,
        paymentStatus: currentFilters.paymentStatus !== 'all' ? currentFilters.paymentStatus : undefined,
        startDate: currentFilters.startDate || undefined,
        endDate: currentFilters.endDate || undefined,
        minAmount: currentFilters.minAmount || undefined,
        maxAmount: currentFilters.maxAmount || undefined,
        isWoodWork: currentFilters.isWoodWork !== 'all' ? currentFilters.isWoodWork : undefined,
        salesperson_id: currentFilters.salesperson_id !== 'all' ? currentFilters.salesperson_id : undefined
      };

      if (currentSearch.trim()) {
        params.search = currentSearch.trim();
      }

      const response = await api.get('/sales/closed-orders', { params });

      // Normalize closed orders to preserve cancellation fields
      const normalizedOrders = (response.data.closedOrders || []).map(order => ({
        ...order,
        cancellation_status: order.cancellation_status || null,
        cancellation_reason: order.cancellation_reason || null,
        cancellation_payment_status: order.cancellation_payment_status || null,
        cancelled_by: order.cancelled_by || null,
        cancelled_at: order.cancelled_at || null
      }));

      setClosedOrders(normalizedOrders);
      const paginationData = response.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false
      };
      currentPageRef.current = paginationData.currentPage || 1;
      setPagination(paginationData);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Failed to load closed orders:', error);
      toast.error('Failed to load closed orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    // Backend now handles search filtering. 
    // We only apply sorting locally here.
    let filtered = closedOrders;

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'customer':
            aValue = (a.customer_name || '').toLowerCase();
            bValue = (b.customer_name || '').toLowerCase();
            break;
          case 'date':
            aValue = new Date(a.date || a.created_at || 0);
            bValue = new Date(b.date || b.created_at || 0);
            break;
          case 'amount':
            aValue = parseFloat(a.totalAmount || a.total_amount || 0);
            bValue = parseFloat(b.totalAmount || b.total_amount || 0);
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredOrders(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleViewOrder = async (order) => {
    try {
      // Fetch full closed order details with payment information
      const response = await api.get(`/sales/closed-orders/${order.id || order._id}`);
      const fullOrder = response.data;
      console.log('👁️ View Order - Full Data:', fullOrder);
      setSelectedOrder(fullOrder);
      setEditingOrder(fullOrder); // Initialize editingOrder for later use
      setShowViewModal(true);
    } catch (error) {
      console.error('Failed to fetch closed order details:', error);
      // Try fallback to estimates endpoint
      try {
        const fallbackResponse = await api.get(`/sales/estimates/${order.id || order._id}`);
        const fallbackOrder = fallbackResponse.data.estimate || fallbackResponse.data;
        console.log('👁️ View Order - Fallback Data:', fallbackOrder);

        // Ensure state is populated
        fallbackOrder.customer_state = fallbackOrder.customer_state || fallbackOrder.party_state_name || fallbackOrder.customer?.state || ((fallbackOrder.customer_gst || fallbackOrder.customer?.gstNumber) ? getStateNameFromGST(fallbackOrder.customer_gst || fallbackOrder.customer?.gstNumber) : '') || '';
        fallbackOrder.customer_gst = fallbackOrder.customer_gst || fallbackOrder.customer?.gstNumber || '';

        setSelectedOrder(fallbackOrder);
        setEditingOrder(fallbackOrder); // Initialize editingOrder for later use
        setShowViewModal(true);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        toast.error('Failed to load closed order details');
        // Final fallback to using the order as-is

        // Ensure state is populated even in final fallback
        order.customer_state = order.customer_state || order.party_state_name || order.customer?.state || ((order.customer_gst || order.customer?.gstNumber) ? getStateNameFromGST(order.customer_gst || order.customer?.gstNumber) : '') || '';
        order.customer_gst = order.customer_gst || order.customer?.gstNumber || '';

        setSelectedOrder(order);
        setEditingOrder(order); // Initialize editingOrder for later use
        setShowViewModal(true);
      }
    }
  };

  const handleAddPayment = (order) => {
    setSelectedOrder(order);
    setPaymentData({
      paymentType: 'cash',
      amount: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (paymentUpdateData) => {
    if (!paymentUpdateData.amount_received || parseFloat(paymentUpdateData.amount_received) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setLoading(true);

    try {
      const response = await api.put(`/sales/closed-orders/${selectedOrder.id || selectedOrder._id}/payment`, {
        payment_type: paymentUpdateData.payment_type,
        amount_received: parseFloat(paymentUpdateData.amount_received),
        notes: paymentUpdateData.notes
      });

      // Get updated data from response
      const updatedData = response.data;
      const finalPaidAmount = updatedData.amountReceived || updatedData.amount_received || 0;
      const totalAmount = selectedOrder.totalAmount || selectedOrder.total_amount || 0;
      const finalStatus = calculatePaymentStatus(finalPaidAmount, totalAmount);

      // Update the selected order for the view modal - THIS IS KEY FOR INSTANT UPDATE
      const updatedOrder = {
        ...selectedOrder,
        amountReceived: finalPaidAmount,
        paymentStatus: finalStatus,
        paid_amount: finalPaidAmount,
        payment_status: finalStatus,
        payments: updatedData.payments || selectedOrder.payments || []
      };
      setSelectedOrder(updatedOrder);

      toast.success('Payment added successfully');
      setShowPaymentModal(false);

      // Emit payment sync event
      paymentSync.notifyClosedOrderPaymentUpdate(updatedOrder);

      // Refresh the list preserving page
      await fetchClosedOrders(pagination.currentPage);
    } catch (error) {
      console.error('Failed to add payment:', error);
      toast.error(error.response?.data?.error || 'Failed to add payment');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await api.get('/admin/company-settings');
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      // Use default settings if fetch fails
      setCompanySettings({
        companyName: 'IDEAL TRENDZ INTERIORS',
        addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
        addressLine2: '',
        city: 'HYDERNAGAR',
        state: 'TELANGANA',
        pincode: '500072',
        contactNumber: '9014814901',
        emailId: 'idealtrendz.int@gmail.com',
        gstNumber: '36AHZPN3524A1Z9',
        panNumber: 'AHZPN3524A',
        bankName: 'HDFC Bank',
        accountHolderName: 'IDEAL TRENDZ INTERIORS',
        accountNumber: '50200012345678',
        ifscCode: 'HDFC0001234',
        branchName: 'Nizampet'
      });
    }
  };

  const fetchEstimates = async () => {
    try {
      const response = await api.get('/sales/estimates');

      console.log('📋 Raw estimates response:', response.data);

      // Get all estimates and filter client-side for better control
      const allEstimates = response.data.estimates || response.data || [];
      console.log('📋 Total estimates received:', allEstimates.length);

      // Debug: Log first few estimates to understand structure
      if (allEstimates.length > 0) {
        console.log('📋 Sample estimate structure:', allEstimates.slice(0, 2));
      }

      // Filter out converted estimates with comprehensive checks
      const unconvertedEstimates = allEstimates.filter(estimate => {
        const status = estimate.status || estimate.estimate_status || estimate.documentStatus;
        const isConverted = estimate.converted || estimate.is_converted || estimate.isConverted;
        const hasClosedOrderNumber = estimate.closed_order_number || estimate.closedOrderNumber;
        const hasInvoiceNumber = estimate.invoice_number || estimate.invoiceNumber;

        // Log each estimate for debugging
        console.log(`📋 Checking estimate ${estimate.estimate_number || estimate._id}:`, {
          status,
          isConverted,
          hasClosedOrderNumber,
          hasInvoiceNumber,
          estimate
        });

        // Exclude if any conversion indicator is present
        const isConvertedEstimate =
          status === 'closed' ||
          status === 'converted' ||
          status === 'invoice' ||
          status === 'closed_order' ||
          status === 'sold' ||
          isConverted === true ||
          hasClosedOrderNumber ||
          hasInvoiceNumber;

        const shouldInclude = !isConvertedEstimate;
        console.log(`📋 Estimate ${estimate.estimate_number || estimate._id} - Include: ${shouldInclude}`);

        return shouldInclude;
      });

      console.log('📋 Filtered unconverted estimates:', unconvertedEstimates.length);
      console.log('📋 Unconverted estimate numbers:', unconvertedEstimates.map(e => e.estimate_number || e._id));

      // Force state update with immediate refresh
      setEstimates([]);
      setFilteredEstimates([]);

      // Use setTimeout to ensure state is cleared before setting new data
      setTimeout(() => {
        setEstimates(unconvertedEstimates);
        setFilteredEstimates(unconvertedEstimates);
      }, 100);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error('Failed to load estimates');
    }
  };


  const calculateInvoiceTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
      const discountAmount = baseAmount * ((item.discount || 0) / 100);
      const taxableAmount = baseAmount - discountAmount;
      const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
      return sum + (taxableAmount + gstAmount);
    }, 0);
  };

  const handleOpenConvertModal = async () => {
    try {
      setLoading(true);
      console.log('🔄 Opening Convert Modal - Fetching latest unconverted estimates...');

      // Use the enhanced fetchEstimates function that properly filters converted estimates
      await fetchEstimates();

      setShowConvertModal(true);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error('Failed to load estimates for conversion');
    } finally {
      setLoading(false);
    }
  };

  const filterEstimates = () => {
    if (!estimateSearchTerm.trim()) {
      setFilteredEstimates(estimates);
      return;
    }

    const filtered = estimates.filter(estimate =>
      estimate.customer_name?.toLowerCase().includes(estimateSearchTerm.toLowerCase()) ||
      estimate.estimate_number?.toLowerCase().includes(estimateSearchTerm.toLowerCase())
    );

    setFilteredEstimates(filtered);
  };

  // Robust item mapping helper for the "Edit Products" modal
  const mapItemsForEditing = (itemsToMap, localCategories, localProducts) => {
    return itemsToMap.map((item, idx) => {
      // Robust category ID extraction
      let catId = item.category_id ||
        item.categoryId ||
        item.category_name ||
        item.categoryName ||
        (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
        '';

      // Robust product ID extraction
      let prodId = item.product_id ||
        item.productId ||
        (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
        '';

      console.log(`📦 Item [${idx}] raw:`, { catId, prodId, itemCategory: item.category, productName: item.product_name });

      // CRITICAL: If catId is still missing but we have a product_id, look up the product's category
      if (!catId && prodId) {
        const foundProd = localProducts.find(p => String(p.id || p._id) === String(prodId));
        if (foundProd) {
          catId = foundProd.category_id || foundProd.categoryId;
        }
      }

      // If catId is a name or missing, try to find the actual ID
      if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
        const foundCat = localCategories.find(c =>
          (c.name || '').toLowerCase() === String(catId || '').toLowerCase() ||
          (c.categoryName || '').toLowerCase() === String(catId || '').toLowerCase() ||
          (c.id || c._id) === String(catId)
        );
        if (foundCat) {
          catId = foundCat.id || foundCat._id;
        } else if (item.product_name && !prodId) {
          // Infer Others if name exists but no ID
          const othersCat = localCategories.find(c =>
            c.isOthers ||
            (c.name || '').toLowerCase() === 'others' ||
            (c.name || '').toLowerCase() === 'other'
          );
          if (othersCat) {
            catId = othersCat.id || othersCat._id;
          }
        }
      }

      const selectedCategory = localCategories.find(c => String(c.id || c._id) === String(catId));
      const isOthers = !!(
        selectedCategory?.isOthers ||
        (selectedCategory?.name || '').toLowerCase() === 'others' ||
        (selectedCategory?.name || '').toLowerCase() === 'other' ||
        item.isOthers ||
        (item.product_name && !prodId) // FORCE isOthers if custom name and no product ID
      );

      return {
        ...item,
        category_id: catId,
        product_id: prodId,
        isOthers: isOthers,
        showFreeText: isOthers && !prodId,
        unit_type: item.unit_type || (isOthers ? 'PCS' : 'SFT'),
        hsn_code: item.hsn_code || (item.productId?.product?.hsnCode) || ''
      };
    });
  };

  const handleEstimateSearch = (e) => {
    setEstimateSearchTerm(e.target.value);
    filterEstimates();
  };

  const handleProductEdit = (index, field, value) => {
    const updatedProducts = [...editingProducts];

    // Handle numeric fields allowing empty string
    let newValue = value;
    if (field === 'quantity' || field === 'unit_price' || field === 'gst_rate' || field === 'discount') {
      newValue = value === '' ? '' : parseFloat(value);
    }

    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: newValue
    };

    // Handle category change
    if (field === 'category_id') {
      const selectedCategory = categories.find(c => String(c.id || c._id) === String(value));
      const isOthers = selectedCategory?.isOthers || false;
      updatedProducts[index].isOthers = isOthers;
      updatedProducts[index].product_id = '';
      updatedProducts[index].product_name = '';
      updatedProducts[index].unit_type = isOthers ? 'PCS' : 'SFT';
      updatedProducts[index].unit_price = 0;
    }

    // Handle dimensions and numeric fields matching InvoicesPage.js
    if (['quantity', 'unit_price', 'gst_rate', 'discount', 'nos', 'length', 'breadth'].includes(field)) {
      const numericValue = value === '' ? '' : parseFloat(value);
      updatedProducts[index][field] = numericValue;

      // Auto-compute quantity if dimensions change
      if (['nos', 'length', 'breadth'].includes(field)) {
        const nos = field === 'nos' ? (value === '' ? 0 : parseFloat(value) || 0) : (updatedProducts[index].nos || 0);
        const length = field === 'length' ? (value === '' ? 0 : parseFloat(value) || 0) : (updatedProducts[index].length || 0);
        const breadth = field === 'breadth' ? (value === '' ? 0 : parseFloat(value) || 0) : (updatedProducts[index].breadth || 0);
        updatedProducts[index].quantity = computeWoodWorkQty(nos, length, breadth);
      }
    } else if (field !== 'category_id') {
      updatedProducts[index][field] = value;
    }

    // Recalculate amounts when relevant fields change
    if (['quantity', 'unit_price', 'gst_rate', 'discount', 'nos', 'length', 'breadth'].includes(field)) {
      // Use 0 for calculations if value is empty string
      const quantity = updatedProducts[index].quantity === '' ? 0 : (updatedProducts[index].quantity || 0);
      const unitPrice = updatedProducts[index].unit_price === '' ? 0 : (updatedProducts[index].unit_price || 0);
      const gstRate = updatedProducts[index].gst_rate === '' ? 0 : (updatedProducts[index].gst_rate || 0);
      const discount = updatedProducts[index].discount === '' ? 0 : (updatedProducts[index].discount || 0);

      // Calculate amounts correctly: Discount first, then GST
      const baseAmount = quantity * unitPrice;
      const discountAmount = baseAmount * (discount / 100);
      const taxableAmount = baseAmount - discountAmount;
      const gstAmount = taxableAmount * (gstRate / 100);
      const totalAmount = taxableAmount + gstAmount;

      // Update calculated fields
      updatedProducts[index].amount = taxableAmount;
      updatedProducts[index].gst_amount = gstAmount;
      updatedProducts[index].total_amount = totalAmount;
      updatedProducts[index].subtotal = totalAmount;
    }

    setEditingProducts(updatedProducts);
  };

  // Apply every field of a picked product in one state update (calling
  // handleProductEdit once per field drops all but the last change).
  // Field names follow getFormData's product mapping (sellingPrice,
  // gstPercentage, hsnCode, dimensions.unit), with older names as fallbacks.
  const handleProductSelect = (index, val, product) => {
    const updatedProducts = [...editingProducts];
    const pick = (...values) => values.find(v => v !== undefined && v !== null && v !== '');
    const item = {
      ...updatedProducts[index],
      product_id: val,
      product_name: product.name || product.productName || '',
      unit_price: parseFloat(pick(product.sellingPrice, product.price, product.product?.sellingPrice)) || 0,
      gst_rate: parseFloat(pick(product.product?.gstPercentage, product.gstPercentage, product.gstRate, 18)) || 0,
      hsn_code: pick(product.product?.hsnCode, product.hsnCode, product.hsn_code) || '',
      unit_type: pick(product.product?.dimensions?.unit, product.unit_type, product.unit) || 'SFT'
    };
    const quantity = parseFloat(item.quantity) || 0;
    const discount = parseFloat(item.discount) || 0;
    const baseAmount = quantity * item.unit_price;
    const taxableAmount = baseAmount - (baseAmount * discount) / 100;
    const gstAmount = (taxableAmount * item.gst_rate) / 100;
    item.amount = taxableAmount;
    item.gst_amount = gstAmount;
    item.total_amount = taxableAmount + gstAmount;
    item.subtotal = taxableAmount + gstAmount;
    updatedProducts[index] = item;
    setEditingProducts(updatedProducts);
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...editingProducts];
    updatedProducts.splice(index, 1);
    setEditingProducts(updatedProducts);
  };

  const handleAddProduct = () => {
    if (isWoodWorkMode) {
      const sectionName = prompt("Enter Section/Category Name (e.g., KITCHEN, WARDROBE):");
      if (!sectionName) return;

      const newItem = {
        categoryLabel: sectionName.toUpperCase(),
        product_name: '',
        unit_price: 1000,
        nos: 1,
        length: 1,
        breadth: 1,
        quantity: 1,
        gst_rate: 0,
        amount: 1000,
        total_amount: 1000,
        unit_type: 'SFT'
      };
      setEditingProducts([...editingProducts, newItem]);
    } else {
      const newItem = {
        product_id: '',
        category_id: '',
        product_name: '',
        quantity: 1,
        unit_price: 0,
        discount: 0,
        gst_rate: 18,
        hsn_code: '',
        unit_type: 'SFT',
        amount: 0,
        gst_amount: 0,
        total_amount: 0,
        subtotal: 0
      };
      setEditingProducts([...editingProducts, newItem]);
    }
  };

  // Close Edit Products without saving and return to the modal that opened it
  // (X, Escape and Discard all use this).
  const closeEditProductsModal = () => {
    setShowEditProductsModal(false);
    if (editModalContext === 'edit') {
      setShowEditClosedOrderModal(true);
    } else if (editModalContext === 'invoiceConvert') {
      setShowConvertToInvoiceModal(true);
    } else if (editModalContext === 'estimateConvert') {
      setShowEstimateDetailModal(true);
    }
  };

  const handleSaveProductEdits = () => {
    // Validate all items have names if free text
    const hasEmptyNames = editingProducts.some(item => item.isOthers && item.showFreeText && !item.product_name?.trim());
    if (hasEmptyNames) {
      toast.error('Please enter product names for all "Other" items');
      return;
    }

    if (editModalContext === 'edit') {
      // Edit Closed Order: write items back to the order being edited
      setEditingOrder({
        ...editingOrder,
        items: recalcItemsAmounts(editingProducts)
      });
      setShowEditProductsModal(false);
      setShowEditClosedOrderModal(true);
    } else if (editModalContext === 'invoiceConvert') {
      // Closed order -> invoice conversion
      const totalAmount = editingProducts.reduce((sum, item) => {
        const baseAmount = item.quantity * item.unit_price;
        const discountAmount = baseAmount * ((item.discount || 0) / 100);
        const taxableAmount = baseAmount - discountAmount;
        const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
        return sum + (taxableAmount + gstAmount);
      }, 0);

      setConvertingClosedOrder({
        ...convertingClosedOrder,
        items: recalcItemsAmounts(editingProducts),
        totalAmount: totalAmount,
        total_amount: totalAmount
      });
      setShowEditProductsModal(false);
      setShowConvertToInvoiceModal(true);
    } else {
      // Estimate -> closed order conversion ('estimateConvert')
      const totalAmount = editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0);

      // Update the selected estimate with edited products
      const updatedEstimate = {
        ...selectedEstimate,
        items: recalcItemsAmounts(editingProducts),
        totalAmount: totalAmount,
        total_amount: totalAmount
      };

      setSelectedEstimate(updatedEstimate);
      setShowEditProductsModal(false);
      setShowEstimateDetailModal(true);
    }
  };

  const handleSelectEstimate = async (estimate) => {
    try {
      setLoading(true);
      // Fetch full estimate details and form data in parallel for robust mapping
      const [response, fetchedFormData] = await Promise.all([
        api.get(`/sales/estimates/${estimate.id || estimate._id}`),
        getFormData()
      ]);

      const fullEstimate = response.data.estimate || response.data || estimate;
      const fetchedCategories = fetchedFormData.categories || [];
      const fetchedProducts = fetchedFormData.products || [];

      // Update reference data if needed
      setCategories(fetchedCategories);
      setProducts(fetchedProducts);

      console.log('📦 Selected Estimate Data:', fullEstimate);

      const itemsWithIds = recalcItemsAmounts(mapItemsForEditing(fullEstimate.items || [], fetchedCategories, fetchedProducts));

      setSelectedEstimate({ ...fullEstimate, items: itemsWithIds });
      setEditingProducts(recalcItemsAmounts(itemsWithIds));
      setClosedOrderData({
        orderDescription: '',
        paymentType: 'cash',
        amountReceived: 0,
        paymentStatus: 'pending',
        dispatched_from: fullEstimate.dispatched_from || '',
        delivered_to: fullEstimate.delivered_to || '',
        termsAndConditions: fullEstimate.termsAndConditions || ''
      });
      setShowEstimateDetailModal(true);
      setShowConvertModal(false);
    } catch (error) {
      console.error('Failed to fetch estimate details:', error);
      toast.error('Failed to load estimate details');
    } finally {
      setLoading(false);
    }
  };

  const handleClosedOrderSubmit = async () => {
    try {
      setLoading(true);

      console.log('🔄 Converting estimate to closed order:', selectedEstimate);
      console.log('📝 Closed order data:', closedOrderData);

      // Validate items before conversion
      const items = recalcItemsAmounts(Array.isArray(selectedEstimate.items) ? selectedEstimate.items : []);

      if (items.length === 0) {
        toast.error('Cannot convert: No products in the estimate');
        setLoading(false);
        return;
      }

      // Note: Removed blocking quantity validation - allow conversion even with zero quantities

      // Proceed with conversion
      await proceedWithEstimateToClosedOrderConversion();

    } catch (error) {
      console.error('❌ Failed to convert estimate to closed order:', error);
      console.error('❌ Error response:', error.response?.data);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to closed order';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Separate function for actual estimate to closed order conversion
  const proceedWithEstimateToClosedOrderConversion = async () => {
    try {
      setLoading(true);

      // Validate items before conversion
      const items = recalcItemsAmounts(Array.isArray(selectedEstimate.items) ? selectedEstimate.items : []);

      // Validate required fields before conversion
      if (!selectedEstimate || (!selectedEstimate.id && !selectedEstimate._id)) {
        toast.error('Invalid estimate selected. Please try again.');
        setLoading(false);
        return;
      }

      // Prepare closed order data with proper validation
      const closedOrderRequest = {
        payment_type: closedOrderData.paymentType || 'cash',
        amount_received: parseFloat(closedOrderData.amountReceived) || 0,
        order_description: closedOrderData.orderDescription || '',
        customer_type: 'B2C',
        payment_status: closedOrderData.paymentStatus || 'pending',
        gst_number: closedOrderData.gstNumber || '',
        dispatched_from: closedOrderData.dispatched_from || '',
        delivered_to: closedOrderData.delivered_to || '',
        termsAndConditions: closedOrderData.termsAndConditions || '',
        user_id: user?.id,
        user_role: user?.role,
        // Include updated items if they were edited
        items: items.map(item => ({
          ...item,
          product_id: item.product_id || item.productId,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0
        }))
      };

      console.log('🔄 Sending conversion request:', {
        estimateId: selectedEstimate.id || selectedEstimate._id,
        request: closedOrderRequest
      });

      // Check and update estimate status if needed before conversion
      const estimateId = selectedEstimate.id || selectedEstimate._id;

      // First, try to update the estimate status to 'open' if it's not already
      try {
        console.log('🔄 Checking estimate status before conversion...');
        const statusUpdateResponse = await api.put(`/sales/estimates/${estimateId}`, {
          status: 'open'
        });
        console.log('✅ Estimate status updated to open:', statusUpdateResponse.data);
      } catch (statusError) {
        console.warn('⚠️ Could not update estimate status:', statusError);
        // Continue with conversion attempt anyway
      }

      // Send request to convert estimate to closed order
      const response = await api.put(
        `/sales/estimates/${estimateId}/close`,
        closedOrderRequest
      );

      console.log('✅ Estimate converted to closed order successfully:', response.data);

      toast.success('Estimate converted to closed order successfully!');
      setShowEstimateDetailModal(false);
      setSelectedEstimate(null);

      // Refresh both closed orders and estimates list
      await fetchClosedOrders();
      await fetchEstimates();

    } catch (error) {
      console.error('❌ Failed to convert estimate to closed order:', error);
      console.error('❌ Error response:', error.response?.data);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to closed order';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Handler for initiating closed order to invoice conversion - SHOWS MODAL FIRST
  const handleConvertClosedOrderToInvoice = async () => {
    try {
      console.log(' Opening Convert to Invoice modal for closed order:', selectedOrder);

      // Validate items before showing modal
      const items = recalcItemsAmounts(Array.isArray(selectedOrder.items) ? selectedOrder.items : []);

      if (items.length === 0) {
        toast.error('Cannot convert: No products in the closed order');
        return;
      }

      // Initialize conversion data
      setConvertingClosedOrder(selectedOrder);
      setConversionEditingCustomer({
        name: selectedOrder.customer_name || '',
        phone: selectedOrder.customer_phone || '',
        email: selectedOrder.customer_email || '',
        address: selectedOrder.customer_address || '',
        state: selectedOrder.customer_state || selectedOrder.party_state_name || selectedOrder.customer?.state || ((selectedOrder.customer_gst || selectedOrder.customer?.gstNumber) ? getStateNameFromGST(selectedOrder.customer_gst || selectedOrder.customer?.gstNumber) : '') || ''
      });
      setConversionPaymentData({
        payment_type: 'cash',
        gst_number: selectedOrder.customer_gst || selectedOrder.customer?.gstNumber || '',
        description: selectedOrder.orderDescription || ''
      });

      // Fetch next invoice number
      setInvoiceNumber('');
      // Load the next invoice number in the background so the modal opens immediately
      api.get('/sales/next-document-number?type=invoice')
        .then(res => { if (res.data?.nextNumber) setInvoiceNumber(prev => prev || res.data.nextNumber); })
        .catch(err => console.error('Failed to fetch next invoice number:', err));

      // Show the Convert to Invoice modal (like Estimates page)
      setShowConvertToInvoiceModal(true);

    } catch (error) {
      console.error(' Error opening Convert to Invoice modal:', error);
      toast.error('Failed to open conversion modal');
    }
  };

  // Handler for saving closed order edits (Admin only)
  const handleSaveOrderEdits = async () => {
    try {
      setLoading(true);

      // Calculate total from edited items
      const calculateTotal = (items) => {
        return items.reduce((sum, item) => {
          const baseAmount = (item.quantity || 0) * (item.unit_price || item.unitPrice || 0);
          const discountAmount = baseAmount * ((item.discount || 0) / 100);
          const taxableAmount = baseAmount - discountAmount;
          const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
          return sum + (taxableAmount + gstAmount);
        }, 0);
      };

      const totalAmount = calculateTotal(editingProducts);

      // Debug salesperson data
      console.log('💾 Salesperson debug (Closed Order):', {
        salespersonId: editingOrder.salespersonId,
        salesperson_id: editingOrder.salesperson_id,
        salesperson: editingOrder.salesperson
      });

      // Extract salesperson ID - try multiple possible paths (Robust Resolution)
      let salespersonId = null;

      // First check if salesperson object exists and extract its ID
      if (editingOrder.salesperson && typeof editingOrder.salesperson === 'object') {
        salespersonId = editingOrder.salesperson.id || editingOrder.salesperson._id;
      } else if (editingOrder.salesperson && typeof editingOrder.salesperson === 'string') {
        salespersonId = editingOrder.salesperson;
      } else if (editingOrder.salespersonId) {
        if (typeof editingOrder.salespersonId === 'object') {
          salespersonId = editingOrder.salespersonId._id || editingOrder.salespersonId.id;
        } else {
          salespersonId = editingOrder.salespersonId;
        }
      } else if (editingOrder.salesperson_id) {
        salespersonId = editingOrder.salesperson_id;
      }

      // Fallback: try to get from selectedOrder if editingOrder doesn't have it
      if (!salespersonId && selectedOrder) {
        if (selectedOrder.salespersonId) {
          if (typeof selectedOrder.salespersonId === 'object') {
            salespersonId = selectedOrder.salespersonId._id || selectedOrder.salespersonId.id;
          } else {
            salespersonId = selectedOrder.salespersonId;
          }
        } else if (selectedOrder.salesperson_id) {
          salespersonId = selectedOrder.salesperson_id;
        } else if (selectedOrder.salesperson && typeof selectedOrder.salesperson === 'object') {
          salespersonId = selectedOrder.salesperson.id || selectedOrder.salesperson._id;
        }
      }

      // Final Fallback: Use logged-in user ID as the salesperson
      if (!salespersonId && user) {
        console.log('💾 Using logged-in user as salesperson fallback for closed order:', user);
        salespersonId = user.id || user._id;
      }

      console.log('💾 Resolved salesperson ID for closed order:', salespersonId);

      // Validate that we have a salesperson ID
      if (!salespersonId) {
        toast.error('Unable to determine salesperson. Please contact support.');
        setLoading(false);
        return;
      }

      // Recalculate payment status based on new total and existing amount received
      const paidAmount = parseFloat(editingOrder.amountReceived || editingOrder.amount_received || selectedOrder?.amountReceived || 0);
      const newPaymentStatus = calculatePaymentStatus(paidAmount, totalAmount);

      console.log('💰 Payment Status Recalculation:', {
        paidAmount,
        totalAmount,
        oldStatus: editingOrder.paymentStatus,
        newStatus: newPaymentStatus
      });

      // Prepare update data using editingOrder as base
      const updateData = {
        salesperson_id: salespersonId,
        customer: {
          name: editingCustomer.name,
          phone: editingCustomer.phone,
          email: editingCustomer.email,
          address: editingCustomer.address,
          gstNumber: editingCustomer.gstNumber || editingOrder.customer_gst || '',
          state: editingCustomer.state || ''
        },
        items: recalcItemsAmounts(editingProducts),
        total_amount: totalAmount,
        paymentStatus: newPaymentStatus, // Include recalculated status
        dispatched_from: editingOrder.dispatched_from || '',
        delivered_to: editingOrder.delivered_to || '',
        termsAndConditions: editingOrder.termsAndConditions || '',
        date: editingOrder.date ? new Date(editingOrder.date).toISOString().split('T')[0] : (editingOrder.createdAt ? new Date(editingOrder.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
      };

      console.log('💾 Saving closed order edits:', updateData);

      // Resolve Closed Order ID - try multiple possible ID properties
      const orderId = editingOrder._id || editingOrder.id;

      if (!orderId) {
        throw new Error('Closed Order ID is missing. Unable to save.');
      }

      // Update the closed order via the dedicated closed orders endpoint
      const response = await api.put(`/sales/closed-orders/${orderId}`, updateData);

      if (response.data) {
        toast.success('Closed order updated successfully');

        // Update the selected order state with fresh data
        const updatedOrder = {
          ...selectedOrder,
          ...editingOrder,
          paymentStatus: newPaymentStatus, // Ensure local state has updated status
          date: editingOrder.date,
          customer_name: editingCustomer.name,
          customer_phone: editingCustomer.phone,
          customer_email: editingCustomer.email,
          customer_address: editingCustomer.address,
          // Ensure customer object is also updated for display/PDF
          customer: {
            ...selectedOrder?.customer,
            name: editingCustomer.name,
            phone: editingCustomer.phone,
            email: editingCustomer.email,
            address: editingCustomer.address,
            gstNumber: editingOrder.customer_gst || selectedOrder?.customer?.gstNumber || ''
          },
          items: editingProducts.map(item => ({
            ...item,
            quantity: parseFloat(item.quantity) || 0,
            unit_price: parseFloat(item.unit_price || item.unitPrice) || 0,
            gst_rate: parseFloat(item.gst_rate) || 0,
            gst_amount: parseFloat(item.gst_amount) || 0,
            amount: parseFloat(item.amount) || 0,
            total_amount: parseFloat(item.total_amount) || 0,
            discount: parseFloat(item.discount) || 0,
            hsn_code: item.hsn_code || '',
            unit_type: item.unit_type || 'PCS'
          })),
          totalAmount: totalAmount,
          total_amount: totalAmount
        };

        setSelectedOrder(updatedOrder);
        setShowEditClosedOrderModal(false);

        // Refresh the closed orders list to ensure consistency across the page
        fetchClosedOrders();
      }
    } catch (error) {
      console.error('❌ Failed to save closed order edits:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  // Handler for when user clicks "Create Invoice" in the modal - THEN CHECK STOCK
  // Handle converting closed order to invoice
  const handleCreateInvoiceFromModal = async () => {
    try {
      setLoading(true);

      const items = recalcItemsAmounts(Array.isArray(convertingClosedOrder.items) ? convertingClosedOrder.items : []);
      if (items.length === 0) {
        toast.error('Cannot convert: No products in the closed order');
        setLoading(false);
        return;
      }

      // Proceed with conversion
      await proceedWithClosedOrderInvoiceConversion();
    } catch (error) {
      console.error(' Error in closed order to invoice conversion:', error);
      toast.error('Failed to convert closed order to invoice');
      setLoading(false);
    }
  };

  // Separate function for actual closed order to invoice conversion
  const proceedWithClosedOrderInvoiceConversion = async () => {
    try {
      setLoading(true);

      // Close the conversion modal immediately to show loading state
      setShowConvertToInvoiceModal(false);

      // Show loading overlay
      const loadingOverlay = document.createElement('div');
      loadingOverlay.id = 'conversion-loading-overlay';
      loadingOverlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]';
      loadingOverlay.innerHTML = `
        <div class="bg-white rounded-2xl p-8 flex flex-col items-center max-w-md">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mb-6"></div>
          <h3 class="text-xl font-bold text-slate-900 mb-2">Converting Closed Order to Invoice</h3>
          <p class="text-slate-600 text-center">Please wait while we process your request...</p>
          <p class="text-slate-500 text-sm mt-4">This may take a few moments</p>
        </div>
      `;
      document.body.appendChild(loadingOverlay);

      // Prepare invoice data with proper structure
      const invoiceData = {
        // Basic info
        closed_order_id: convertingClosedOrder.id || convertingClosedOrder._id,
        salesperson_id: convertingClosedOrder.salesperson?.id || convertingClosedOrder.salesperson_id,
        date: conversionPaymentData.date || convertingClosedOrder.date || new Date().toISOString().split('T')[0],

        // Customer info
        customer_name: conversionEditingCustomer.name || convertingClosedOrder.customer_name || 'Walk-in Customer',
        customer_phone: conversionEditingCustomer.phone || convertingClosedOrder.customer_phone || '9999999999',
        customer_email: conversionEditingCustomer.email || convertingClosedOrder.customer_email || '',
        customer_address: conversionEditingCustomer.address || convertingClosedOrder.customer_address || '',
        customer_gst: conversionPaymentData.gst_number || convertingClosedOrder.customer_gst || '',
        gstNumber: conversionPaymentData.gst_number || '',

        // Items
        // Recalculate amount / gst_amount / total_amount / subtotal from qty, price,
        // discount and gst_rate so stale stored values are never sent.
        items: recalcItemsAmounts((convertingClosedOrder.items || []).map((item, idx) => ({
          category_id: item.category_id || '',
          product_id: item.product_id || '',
          product_name: item.product_name || '',
          quantity: item.quantity || 0,
          unit_price: item.unit_price || 0,
          unit_type: item.unit_type || 'SFT',
          hsn_code: item.hsn_code || '',
          discount: item.discount || 0,
          gst_rate: (item.gst_rate !== undefined && item.gst_rate !== null) ? item.gst_rate : 0,
          // Preserve Wood Work dimension fields
          nos: item.nos !== undefined ? item.nos : null,
          length: item.length !== undefined ? item.length : null,
          breadth: item.breadth !== undefined ? item.breadth : null,
          isWoodWork: !!(item.isWoodWork),
          categoryLabel: item.categoryLabel || ''
        }))),

        // Payment info
        payment_type: conversionPaymentData.payment_type || 'cash',
        description: conversionPaymentData.description || '',
        dispatched_from: convertingClosedOrder.dispatched_from || '',
        delivered_to: convertingClosedOrder.delivered_to || ''
      };

      console.log('📤 Sending invoice data from closed order:', invoiceData);
      console.log('📤 Invoice items details:', invoiceData.items);

      // Call backend API to convert closed order to invoice
      // Pass the existing payment data from the closed order + any additional payment
      const response = await api.put(`/sales/closed-orders/${convertingClosedOrder.id || convertingClosedOrder._id}/invoice`, {
        invoice_number: invoiceNumber,
        payment_type: conversionPaymentData.payment_type,
        amount_received: parseFloat(conversionPaymentData.amount_received) || 0,
        payment_status: conversionPaymentData.payment_status,
        invoice_description: conversionPaymentData.description,
        gst_number: conversionPaymentData.gst_number,
        termsAndConditions: includeTermsClosedOrderModal ? (conversionPaymentData.termsAndConditions || '') : '',
        items: invoiceData.items,
        salesperson_id: invoiceData.salesperson_id,
        salesperson_name: convertingClosedOrder.salesperson?.name || convertingClosedOrder.salesperson_name || user?.name || 'Unknown User',
        dispatched_from: conversionPaymentData.dispatched_from,
        delivered_to: conversionPaymentData.delivered_to,
        user_id: user?.id || user?._id,
        date: invoiceData.date
      });

      console.log('✅ Closed order converted to invoice successfully:', response.data);

      const createdInvoiceNumber = response.data.invoice?.invoice_number || response.data.invoice_number || 'N/A';
      const invoiceId = response.data.invoice?.id || response.data.invoice?._id;

      // Show success toast with proper message
      toast.success(`Invoice created successfully! Invoice #${createdInvoiceNumber}`);

      // Remove loading overlay
      const overlay = document.getElementById('conversion-loading-overlay');
      if (overlay) overlay.remove();

      // Close all modals
      setShowViewModal(false);
      setConvertingClosedOrder(null);
      setSelectedOrder(null);

      // Refresh the closed orders list to get updated data
      await fetchClosedOrders();

      // Navigate to Invoices page
      setTimeout(() => {
        navigate('/admin/invoices');
      }, 1000); // Small delay to ensure toast is visible

    } catch (error) {
      console.error('❌ Failed to convert closed order to invoice:', error);
      console.error('❌ Error response:', error.response?.data);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert closed order to invoice';

      toast.error(`Conversion failed: ${errorMessage}`);

      // Remove loading overlay
      const overlay = document.getElementById('conversion-loading-overlay');
      if (overlay) overlay.remove();

      // Reopen the modal on error so user can retry
      setShowConvertToInvoiceModal(true);
    } finally {
      setLoading(false);
    }
  };



  // Cancellation handlers
  const handleCancelClosedOrder = () => {
    setShowCancellationModal(true);
  };

  const handleConfirmCancellation = async (cancellationData) => {
    try {
      setCancellationLoading(true);

      const response = await api.put(
        `/sales/closed-orders/${selectedOrder.id}/cancel`,
        {
          cancellation_reason: cancellationData.reason,
          cancellation_payment_status: cancellationData.paymentStatus,
          cancelled_by: 'Current User' // You can get this from auth context
        }
      );

      if (response.data.success) {
        // Update the selected order with cancellation data from backend response
        const updatedOrder = {
          ...selectedOrder,
          status: response.data.closedOrder?.status || 'cancelled',
          cancellation_status: response.data.closedOrder?.cancellation_status || 'cancelled',
          cancellation_reason: response.data.closedOrder?.cancellation_reason || cancellationData.reason,
          cancellation_payment_status: response.data.closedOrder?.cancellation_payment_status || cancellationData.paymentStatus,
          cancelled_at: response.data.closedOrder?.cancelled_at || new Date().toISOString(),
          cancelled_by: response.data.closedOrder?.cancelled_by || 'Current User'
        };

        setSelectedOrder(updatedOrder);

        // Refresh the closed orders list
        fetchClosedOrders();

        toast.success('Closed order cancelled successfully');
        setShowCancellationModal(false);
      } else {
        throw new Error(response.data.message || 'Failed to cancel closed order');
      }
    } catch (error) {
      console.error('Failed to cancel closed order:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel closed order');
    } finally {
      setCancellationLoading(false);
    }
  };

  // Handler to update payment status for cancelled orders
  const handleUpdatePaymentStatus = async () => {
    if (!paymentStatusUpdate) {
      toast.error('Please select a payment status');
      return;
    }

    try {
      setPaymentStatusUpdateLoading(true);

      const response = await api.put(
        `/sales/closed-orders/${selectedOrder.id}/payment-status`,
        {
          cancellation_payment_status: paymentStatusUpdate
        }
      );

      if (response.data.success) {
        // Update the selected order with new payment status
        const updatedOrder = {
          ...selectedOrder,
          cancellation_payment_status: paymentStatusUpdate
        };

        setSelectedOrder(updatedOrder);

        // Refresh the closed orders list
        fetchClosedOrders();

        toast.success('Payment status updated successfully');
        setShowPaymentStatusUpdateModal(false);
        setPaymentStatusUpdate('');
      } else {
        throw new Error(response.data.message || 'Failed to update payment status');
      }
    } catch (error) {
      console.error('Failed to update payment status:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment status');
    } finally {
      setPaymentStatusUpdateLoading(false);
    }
  };

  // Delete Closed Order Handler
  const handleDeleteClosedOrder = (order) => {
    setDeletingOrder(order);
    setShowDeleteModal(true);
  };

  const confirmDeleteClosedOrder = async () => {
    try {
      const loadingToast = toast.loading('Deleting closed order...');
      await api.delete(`/sales/closed-orders/${deletingOrder.id || deletingOrder._id}`);
      toast.dismiss(loadingToast);
      toast.success('Closed order deleted successfully');
      setShowDeleteModal(false);
      setDeletingOrder(null);
      await fetchClosedOrders(); // Refresh the list
    } catch (error) {
      console.error('Delete error:', error);
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Failed to delete closed order');
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading closed orders...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 md:pb-8">
      <div className="mx-auto bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 ease-in-out p-4 md:p-8" style={{ width: '98%', maxWidth: '1400px', margin: '10px auto' }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A5F38] mb-4">
            Estimate Closed Orders
          </h1>
          <p className="text-lg text-slate-600 font-medium">Manage B2C orders converted from estimates</p>
        </div>

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          {/* Left Side: Buttons */}
          <div className="flex flex-row gap-4">
            <button
              onClick={handleOpenConvertModal}
              className="px-6 py-3 bg-white border border-[#C5A57D] text-[#0A5F38] font-semibold rounded-xl hover:bg-[#C5A57D]/10 transition-colors flex items-center h-full"
            >
              <Plus className="h-5 w-5 mr-2" />
              Convert Estimate
            </button>
          </div>

          {/* Right Side: Search */}
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto items-center">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search closed orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 w-full border border-[rgb(203,213,225)] rounded-2xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-[#F9F9F7] shadow-sm text-slate-700 placeholder-slate-400"
              />
            </div>
          </div>
        </div>
        
        <AdvancedFilters
          filters={filters}
          setFilters={setFilters}
          showSalesperson={true}
          salespersons={salespersons}
          onClear={() => setFilters({
            dateRange: 'all',
            status: 'all',
            paymentStatus: 'all',
            startDate: '',
            endDate: '',
            minAmount: '',
            maxAmount: '',
            isWoodWork: 'all',
            salesperson_id: 'all'
          })}
          showPaymentStatus={true}
          statusOptions={[
            { value: 'all', label: 'All Status' },
            { value: 'closed_order', label: 'Closed Orders' },
            { value: 'converted_to_invoice', label: 'Converted to Invoices' },
            { value: 'cancelled', label: 'Cancelled' }
          ]}
        />

        {/* Closed Orders Table */}
        <div className="bg-[#E8DCC4] p-4 md:p-[38px] rounded-3xl">
          <div className="bg-[#F9F6F0] rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/60 overflow-hidden">
            <div className="overflow-x-auto">
              {filteredOrders.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Closed Orders Found</h3>
                  <p className="text-slate-500">
                    {searchTerm
                      ? 'No orders match your search criteria.'
                      : 'No closed orders found. Convert an estimate to create your first closed order.'}
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-[#005C2D]">
                    <tr>
                      <th
                        className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                        onClick={() => handleSort('date')}
                      >
                        <div className="flex items-center justify-center space-x-1">
                          <span>Date</span>
                          {sortConfig.key === 'date' && (
                            <span className="text-white">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                        Closed Order Number
                      </th>
                      <th
                        className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                        onClick={() => handleSort('customer')}
                      >
                        <div className="flex items-center justify-center space-x-1">
                          <span>Customer /<br />Company Name</span>
                          {sortConfig.key === 'customer' && (
                            <span className="text-white">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                        onClick={() => handleSort('amount')}
                      >
                        <div className="flex items-center justify-center space-x-1">
                          <span>Amount</span>
                          {sortConfig.key === 'amount' && (
                            <span className="text-white">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                        Status
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                        Payment Status
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {filteredOrders.map((order, index) => (
                      <tr key={order.id} className={`transition-colors duration-200 ${index % 2 === 0
                        ? 'bg-white hover:bg-[#F3EFE0]/50'
                        : 'bg-[#F3EFE0] hover:bg-[#E8DCC4]/50'
                        }`}>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                          {order.date ?
                            new Date(order.date).toLocaleDateString('en-IN') :
                            order.createdAt ?
                              new Date(order.createdAt).toLocaleDateString('en-IN') :
                              new Date().toLocaleDateString('en-IN')
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-semibold text-slate-900">
                            {(order.closed_order_number || order._id?.slice(-8) || 'CO' + order.id).replace(/-CANCEL-\d+/g, ' (Cancelled)')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-normal break-words max-w-[250px] text-center">
                          <div className="text-sm font-medium text-slate-900 break-words">
                            {order.customer_name || 'Walk-in Customer'}
                          </div>
                          <div className="text-sm text-slate-500">
                            {order.customer_phone || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-semibold text-slate-900">
                            {formatCurrency(order.totalAmount || order.total_amount)}
                          </div>
                          <div className="text-sm text-slate-500">
                            Paid: {formatCurrency(order.amountReceived || order.amount_received)}
                          </div>
                        </td>
                        {/* Status Column - Conversion Status and Cancellation Status */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="space-y-1 flex flex-col items-center">
                            {order.invoice_cancelled || order.cancelled_invoice ? (
                              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                Invoice Cancelled
                              </span>
                            ) : order.cancellation_status === 'cancelled' || order.status === 'cancelled' || order.cancelled_closed_order || order.closed_order_cancelled ? (
                              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                Cancelled
                              </span>
                            ) : order.is_converted && (order.converted_to_invoice_id || order.invoice_number) ? (
                              <div className="space-y-1">
                                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#E7F4E9] text-[#005C2D]">
                                  Converted to Invoice
                                </span>
                                <div className="text-xs">
                                  → <InvoiceNumberDisplay
                                    closedOrderId={order.id || order._id}
                                    invoiceId={order.converted_to_invoice_id}
                                    companySettings={companySettings}
                                  />
                                </div>
                              </div>
                            ) : (
                              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#E0F2FE] text-[#0369A1]">
                                Not Converted
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Payment Status Column - Show different content based on conversion status and cancellation */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="space-y-1 flex flex-col items-center">
                            {/* If cancelled, show cancellation payment status */}
                            {order.cancellation_status === 'cancelled' || order.status === 'cancelled' ? (
                              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                                {order.cancellation_payment_status === 'refund_initiated' && 'Refund Initiated'}
                                {order.cancellation_payment_status === 'refund_completed' && 'Refund Completed'}
                                {order.cancellation_payment_status === 'no_payment' && 'No Payment'}
                                {!order.cancellation_payment_status && 'Cancelled'}
                              </span>
                            ) : (order.is_converted || order.converted_to_invoice_id || order.invoice_number || order.conversion_status === 'converted_to_invoice') ? (
                              /* If converted to invoice, show badge with link */
                              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                See Invoice
                              </span>
                            ) : (
                              /* Show normal payment status for unconverted orders */
                              order.paymentStatus === 'paid' || order.payment_status === 'paid' ? (
                                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                  Fully Paid
                                </span>
                              ) : order.paymentStatus === 'partial' || order.payment_status === 'partial' ? (
                                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                  Partially Paid
                                </span>
                              ) : (
                                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">
                                  Pending
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex justify-center space-x-1">
                            <button
                              onClick={() => handleViewOrder(order)}
                              className="p-2 bg-[#C5A57D]/10 text-[#0A5F38] hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <DocumentHistoryButton docId={order.id || order._id} />
                            <button
                              onClick={async () => {
                                try {
                                  // Use the order data directly from the table (it already has all the info)
                                  console.log('📄 PDF Download - Order Data:', order);
                                  await generatePDF(order, 'closed_order', companySettings);
                                } catch (error) {
                                  console.error('Failed to generate PDF:', error);
                                  toast.error('Failed to generate PDF');
                                }
                              }}
                              className="p-2 bg-[#C5A57D]/10 text-[#0A5F38] hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                              title="Download PDF"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (!order?.shareToken) {
                                  toast.error('Share token not available yet.');
                                  return;
                                }
                                const guestLink = `${window.location.origin}/view/document/${order.shareToken}`;
                                navigator.clipboard.writeText(guestLink)
                                  .then(() => toast.success('Guest link copied to clipboard!'))
                                  .catch(() => toast.error('Failed to copy link'));
                              }}
                              className="p-2 bg-[#C5A57D]/10 text-[#0A5F38] hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                              title="Copy Guest Link"
                            >
                              <Link2 className="h-4 w-4" />
                            </button>
                            {user?.role === 'admin' && (
                              <button
                                onClick={() => handleDeleteClosedOrder(order)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Closed Order"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalItems > 0 && (
              <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-slate-200">
                <div className="text-sm text-slate-600">
                  Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                  {pagination.totalItems} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchClosedOrders(pagination.currentPage - 1)}
                    disabled={!pagination.hasPreviousPage}
                    className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => fetchClosedOrders(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* View Order Modal */}
        {showViewModal && selectedOrder && (
          <AppModal
            onClose={() => setShowViewModal(false)}
            closeOnEsc={!showPaymentModal && !showCancellationModal}
            title="Closed Order Details"
            size="lg" compactFooter
            footer={
              <>
                    {/* Edit Products Button - Only visible to Admin and not converted to Invoice */}
                    {user && user.role === 'admin' &&
                      selectedOrder?.cancellation_status !== 'cancelled' &&
                      selectedOrder?.status !== 'cancelled' &&
                      !selectedOrder?.is_converted &&
                      !selectedOrder?.converted_to_invoice_id &&
                      !selectedOrder?.invoice_number &&
                      selectedOrder?.conversion_status !== 'converted_to_invoice' && (
                        <button
                          onClick={async () => {
                            // Fetch data locally to avoid state staleness issues during mapping
                            let localCategories = categories;
                            let localProducts = products;

                            if (!localCategories.length || !localProducts.length) {
                              try {
                                const data = await getFormData();
                                localCategories = data.categories || [];
                                localProducts = data.products || [];
                                setCategories(localCategories);
                                setProducts(localProducts);
                              } catch (err) {
                                console.error("Failed to load products/categories", err);
                              }
                            }

                            // Set up editing state
                            setEditingOrder(selectedOrder);
                            setEditingCustomer({
                              name: selectedOrder.customer_name || '',
                              phone: selectedOrder.customer_phone || '',
                              email: selectedOrder.customer_email || '',
                              address: selectedOrder.customer_address || '',
                                      state: selectedOrder.customer_state || selectedOrder.party_state_name || selectedOrder.customer?.state || ((selectedOrder.customer_gst || selectedOrder.customer?.gstNumber) ? getStateNameFromGST(selectedOrder.customer_gst || selectedOrder.customer?.gstNumber) : '') || ''
                            });

                            // Prepare products for editing
                            const items = selectedOrder.items || [];
                            const itemsWithIds = recalcItemsAmounts(mapItemsForEditing(items, localCategories, localProducts));

                            console.log('✨ Mapped items for edit modal:', itemsWithIds);
                            setEditingProducts(recalcItemsAmounts(itemsWithIds));
                            setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                            setIsWoodWorkMode(!!(selectedOrder?.isWoodWork || selectedOrder?.is_wood_work));

                            // Close view modal and open edit modal
                            setShowViewModal(false);
                            setShowEditClosedOrderModal(true);
                          }}
                          className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Edit Products</span>
                        </button>
                      )}
                    {/* Convert to Invoice Button or Converted Status */}
                    {(!selectedOrder.is_converted && !selectedOrder.converted_to_invoice_id && !selectedOrder.invoice_number && selectedOrder.conversion_status !== 'converted_to_invoice' && selectedOrder?.cancellation_status !== 'cancelled' && selectedOrder?.status !== 'cancelled') ? (
                      <button
                        onClick={() => {
                          handleConvertClosedOrderToInvoice();
                        }}
                        disabled={loading}
                        className="px-6 py-2 bg-[#C5A57D] text-white rounded-lg hover:bg-[#B8985E] transition-colors disabled:opacity-50 flex items-center space-x-2"
                      >
                        <FileText className="h-4 w-4" />
                        <span>{loading ? 'Converting...' : 'Convert to Invoice'}</span>
                      </button>
                    ) : (
                      <div className="px-6 py-2 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-center space-x-2">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Converted to Invoice – {selectedOrder.invoice_number || 'INV-XXXX'}</span>
                      </div>
                    )}
                    {/* Update Payment Button - Only show if not converted to invoice and not fully paid and not cancelled */}
                    {!selectedOrder.is_converted &&
                      !selectedOrder.converted_to_invoice_id &&
                      !selectedOrder.invoice_number &&
                      selectedOrder.conversion_status !== 'converted_to_invoice' &&
                      selectedOrder.paymentStatus !== 'paid' &&
                      selectedOrder.payment_status !== 'paid' &&
                      selectedOrder?.cancellation_status !== 'cancelled' &&
                      selectedOrder?.status !== 'cancelled' && (
                        <button
                          onClick={() => {
                            handleAddPayment(selectedOrder);
                          }}
                          className="px-6 py-2 bg-[#C5A57D] text-white rounded-lg hover:bg-[#B8985E] transition-colors flex items-center space-x-2"
                        >
                          <DollarSign className="h-4 w-4" />
                          <span>Update Payment</span>
                        </button>
                      )}
                    {selectedOrder?.cancellation_status !== 'cancelled' &&
                      selectedOrder?.status !== 'cancelled' &&
                      !selectedOrder?.is_converted &&
                      !selectedOrder?.converted_to_invoice_id &&
                      !selectedOrder?.invoice_number &&
                      selectedOrder?.conversion_status !== 'converted_to_invoice' && (
                        <button
                          onClick={handleCancelClosedOrder}
                          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Cancel Closed Order
                        </button>
                      )}
                    {selectedOrder?.cancellation_status === 'cancelled' || selectedOrder?.status === 'cancelled' ? (
                      <>
                        <div className="px-6 py-2 bg-red-100 text-red-800 rounded-lg font-medium">
                          Closed Order Cancelled
                        </div>
                        <button
                          onClick={() => {
                            setShowPaymentStatusUpdateModal(true);
                          }}
                          className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
                        >
                          <DollarSign className="h-4 w-4" />
                          <span>Update Payment Status</span>
                        </button>
                      </>
                    ) : null}
                    {(selectedOrder?.is_converted || selectedOrder?.converted_to_invoice_id || selectedOrder?.invoice_number || selectedOrder?.conversion_status === 'converted_to_invoice') &&
                      selectedOrder?.cancellation_status !== 'cancelled' &&
                      selectedOrder?.status !== 'cancelled' ? (
                      <div className="px-6 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium">
                        Converted to Invoice – Cancel in Invoices Module
                      </div>
                    ) : null}
                    <button
                      onClick={async () => {
                        console.log('📄 PDF Download from Modal - Order Data:', selectedOrder);
                        await generatePDF(selectedOrder, 'closed_order', companySettings);
                      }}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Download PDF
                    </button>
                    <button
                       onClick={() => {
                         if (!selectedOrder?.shareToken) {
                           toast.error('Share token not available yet. Please reopen this modal.');
                           return;
                         }
                         const guestLink = `${window.location.origin}/view/document/${selectedOrder.shareToken}`;
                         navigator.clipboard.writeText(guestLink)
                           .then(() => toast.success('Guest link copied to clipboard!'))
                           .catch(() => toast.error('Failed to copy link'));
                       }}
                       className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                     >
                       <Link2 className="h-4 w-4" />
                       <span>Copy Guest Link</span>
                     </button>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors"
                    >
                      Close
                    </button>
              </>
            }
          >
                {/* PDF-Style Layout */}
                <div className="overflow-x-auto">
                <div className="border-2 border-black bg-white min-w-[760px]">
                  {/* Header Section */}
                  <div className="border-b-2 border-black p-6 bg-white">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-[#C5A57D] rounded-lg flex items-center justify-center overflow-hidden">
                          <img
                            src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                            alt="Company Logo"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <span className="text-white font-bold text-xl" style={{ display: 'none' }} id="logo-fallback">IT</span>
                        </div>
                        <div>
                          <h1 className="text-2xl font-bold text-slate-900">{companySettings?.companyName || 'IDEAL TRENDZ INTERIORS'}</h1>
                          <div className="text-sm text-slate-600 mt-1">
                            <div>{companySettings?.addressLine1 || '# Vertex Privilege, Villa No. 32, Nizampet Road'}</div>
                            {companySettings?.addressLine2 && <div>{companySettings.addressLine2}</div>}
                            <div>{companySettings?.city || 'HYDERNAGAR'}, {companySettings?.state || 'TELANGANA'} - {companySettings?.pincode || '500072'}</div>
                            <div>Email: {companySettings?.emailId || 'idealtrendz.int@gmail.com'}</div>
                            <div>Contact No. {companySettings?.contactNumber || '9014814901'}</div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <div><strong>GST:</strong> {companySettings?.gstNumber || 'N/A'}</div>
                        <div><strong>PAN:</strong> {companySettings?.panNumber || 'N/A'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Document Title */}
                  <div className="border-b-2 border-black p-4 text-center bg-white">
                    <h2 className="text-xl font-bold text-[#0A5F38]">CLOSED ORDER</h2>
                  </div>

                  {/* Customer and Document Info */}
                  <div className="border-b-2 border-black p-6 bg-white">
                    <div className="grid grid-cols-2 gap-8">
                      {/* Left Side - Customer Info */}
                      <div>
                        <div className="mb-4">
                          <div className="font-semibold text-slate-900 mb-2">To</div>
                          <div className="font-bold text-lg">{selectedOrder.customer_name || 'WALK-IN CUSTOMER'}</div>
                          <div className="text-slate-600">{selectedOrder.customer_address || selectedOrder.customer_city || ''}</div>
                          {selectedOrder.customer_phone && (
                            <div className="text-slate-600 mt-1">Phone: {selectedOrder.customer_phone}</div>
                          )}
                          {selectedOrder.customer_email && (
                            <div className="text-slate-600 mt-1">Email: {selectedOrder.customer_email}</div>
                          )}
                        </div>
                      </div>

                      {/* Right Side - Document Info with Fixed Alignment - Shifted Right */}
                      <div className="ml-auto pr-0">
                        <div className="space-y-2">
                          <div className="flex gap-4">
                            <span className="font-semibold w-40">DATE:</span>
                            <span className="flex-1 break-words">{selectedOrder.date ? new Date(selectedOrder.date).toLocaleDateString('en-GB') : selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</span>
                          </div>
                          <div className="flex gap-4">
                            <span className="font-semibold w-40">ORDER No.:</span>
                            <span className="flex-1 break-words">{(selectedOrder.closed_order_number || selectedOrder._id?.slice(-8) || 'ORD001').replace(/-CANCEL-\d+/g, ' (Cancelled)')}</span>
                          </div>
                          <div className="flex gap-4">
                            <span className="font-semibold w-40">PARTY PAN No.:</span>
                            <span className="flex-1 break-words">{selectedOrder.party_pan_no || ''}</span>
                          </div>
                          <div className="flex gap-4">
                            <span className="font-semibold w-40">PARTY GST No.:</span>
                            <span className="flex-1 break-words">{selectedOrder.customer_gst || selectedOrder.party_gst_no || ''}</span>
                          </div>
                          <div className="flex gap-4">
                            <span className="font-semibold w-40">PARTY STATE NAME:</span>
                            <span className="flex-1 break-words">{selectedOrder.customer_state || selectedOrder.party_state_name || ''}</span>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Description */}
                  {selectedOrder.orderDescription && (
                    <div className="border-b border-slate-300 p-6">
                      <div className="mb-2">
                        <div className="font-semibold text-slate-900">Order Description:</div>
                        <div className="text-slate-700 mt-1">{selectedOrder.orderDescription}</div>
                      </div>
                    </div>
                  )}

                  {/* Cancellation Details - Show if order is cancelled */}
                  {(selectedOrder.cancellation_status === 'cancelled' || selectedOrder.status === 'cancelled' || selectedOrder.invoice_cancelled || selectedOrder.cancelled_invoice) && (
                    <div className="border-b border-slate-300 p-6 bg-red-50">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2 mb-4">
                          <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                          <h3 className="font-semibold text-red-900">
                            {selectedOrder.invoice_cancelled || selectedOrder.cancelled_invoice ? 'Invoice Cancelled - Associated Invoice was Cancelled' : 'Cancellation Details'}
                          </h3>
                        </div>
                        {selectedOrder.cancellation_reason && (
                          <div>
                            <span className="font-semibold text-slate-700">Reason:</span>
                            <span className="text-slate-600 ml-2">{selectedOrder.cancellation_reason}</span>
                          </div>
                        )}
                        {selectedOrder.cancellation_payment_status && (
                          <div>
                            <span className="font-semibold text-slate-700">Payment Status:</span>
                            <span className="text-slate-600 ml-2">
                              {selectedOrder.cancellation_payment_status === 'refund_initiated' && 'Refund Initiated – Refund process started'}
                              {selectedOrder.cancellation_payment_status === 'refund_completed' && 'Refund Completed – Refund processed successfully'}
                              {selectedOrder.cancellation_payment_status === 'no_payment' && 'Cancelled (No Payment) – No refund needed'}
                            </span>
                          </div>
                        )}
                        {selectedOrder.cancelled_at && (
                          <div>
                            <span className="font-semibold text-slate-700">Cancelled At:</span>
                            <span className="text-slate-600 ml-2">{new Date(selectedOrder.cancelled_at).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                    {/* Product Table */}
                    {(() => {
                      // Check if any item has a discount
                      const hasDiscounts = selectedOrder.items?.some(item => (item.discount || 0) > 0) || false;

                      return (
                        <div className="border border-slate-300 overflow-x-auto">
                          <table className="w-full">
                            {selectedOrder.isWoodWork ? (
                              /* ══ WOOD WORK VIEW MODAL TABLE ══ */
                              <>
                                <thead>
                                  <tr className="bg-[#0A5F38] border-b border-black">
                                    <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '50px' }}>SI</th>
                                    <th className="border-r border-black p-3 text-left text-xs font-bold text-white uppercase">Type</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '100px' }}>Rate (₹)</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '65px' }}>Nos</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>L</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>B</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '90px' }}>Qty</th>
                                    <th className="p-3 text-right text-xs font-bold text-white uppercase" style={{ width: '120px' }}>Amount (₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                                    const rows = [];
                                    const items = selectedOrder.items || [];

                                    // Build ordered list of unique categories
                                    const categoryOrder = [];
                                    items.forEach(item => {
                                      const cat = item.categoryLabel || '';
                                      if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                                    });

                                    categoryOrder.forEach((catLabel) => {
                                      const catIdx = categoryOrder.indexOf(catLabel);
                                      const catEntries = items.filter(item => (item.categoryLabel || '') === catLabel);

                                      // Category header row
                                      rows.push(
                                        <tr key={'cat-view-' + catLabel} style={{ backgroundColor: '#BDD7EE' }}>
                                          <td className="border-r border-black px-3 py-2 text-center font-bold text-slate-900 border-b border-black">
                                            {CAT_LETTERS[catIdx] || (catIdx + 1)}
                                          </td>
                                          <td colSpan="6" className="border-r border-black px-3 py-2 font-bold text-slate-900 uppercase tracking-wider border-b border-black">
                                            {catLabel}
                                          </td>
                                          <td className="px-3 py-2 border-b border-black"></td>
                                        </tr>
                                      );

                                      // Item rows
                                      catEntries.forEach((item, siNo) => {
                                        const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                        const rate = item.unit_price || 0;
                                        const amount = item.amount || (qty * rate);

                                        rows.push(
                                          <tr key={'item-view-' + (item._id || siNo)} className="border-b border-black hover:bg-slate-50 transition-colors">
                                            <td className="border-r border-black px-2 py-2 text-center text-slate-600">{siNo + 1}</td>
                                            <td className="border-r border-black px-3 py-2 font-medium break-words max-w-[300px]">
                                              {item.product_name || 'Unnamed Item'}
                                            </td>
                                            <td className="border-r border-black px-2 py-2 text-center text-slate-600">
                                              {rate.toLocaleString('en-IN', { minimumFractionDigits: 1 })}
                                            </td>
                                            <td className="border-r border-black px-2 py-2 text-center text-slate-600">{item.nos || 0}</td>
                                            <td className="border-r border-black px-2 py-2 text-center text-slate-600">{item.length || 0}</td>
                                            <td className="border-r border-black px-2 py-2 text-center text-slate-600">{item.breadth || 0}</td>
                                            <td className="border-r border-black px-2 py-2 text-center text-slate-900 font-semibold">{qty}</td>
                                            <td className="px-3 py-2 text-right text-slate-900 font-bold">
                                              {amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                          </tr>
                                        );
                                      });
                                    });

                                    return rows;
                                  })()}
                                </tbody>
                              </>
                            ) : (
                              /* ══ STANDARD RETAIL VIEW MODAL TABLE ══ */
                              <>
                                <thead>
                                  <tr className="bg-[#0A5F38] border-b border-black">
                                    <th className="border-r border-black p-3 text-left text-xs font-semibold text-white uppercase">S.No</th>
                                    <th className="border-r border-black p-3 text-left text-xs font-semibold text-white uppercase">Product Description</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">HSN Code</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Unit</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">QTY</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Rate</th>
                                    {hasDiscounts && (
                                      <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Discount %</th>
                                    )}
                                    <th className="border-r border-black p-3 text-right text-xs font-semibold text-white uppercase">Amount</th>
                                    <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">GST %</th>
                                    <th className="border-r border-black p-3 text-right text-xs font-semibold text-white uppercase">GST Amount</th>
                                    <th className="p-3 text-right text-xs font-semibold text-white uppercase">Total Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                                    selectedOrder.items.map((item, index) => {
                                      const amount = (item.quantity || 0) * (item.unit_price || 0);
                                      const gstRate = item.gst_rate !== undefined ? item.gst_rate : 18;
                                      const gstAmount = (amount * gstRate) / 100;
                                      const totalAmount = amount + gstAmount;

                                      return (
                                        <tr key={index} className="border-b border-slate-200">
                                          <td className="border-r border-slate-300 p-3 text-center break-words">{index + 1}</td>
                                          <td className="border-r border-slate-300 p-3 break-words">{item.product_name || `Product #${index + 1}`}</td>
                                          <td className="border-r border-slate-300 p-3 text-center break-words">{item.hsn_code || '1568'}</td>
                                          <td className="border-r border-slate-300 p-3 text-center break-words">{item.unit_type || 'SFT'}</td>
                                          <td className="border-r border-slate-300 p-3 text-center break-words">{item.quantity || 0}</td>
                                          <td className="border-r border-slate-300 p-3 text-center break-words">{(item.unit_price || 0).toFixed(1)}</td>
                                          {hasDiscounts && (
                                            <td className="border-r border-slate-300 p-3 text-center break-words">{item.discount || 0}%</td>
                                          )}
                                          <td className="border-r border-slate-300 p-3 text-right break-words">{amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                          <td className="border-r border-slate-300 p-3 text-center break-words">{gstRate}</td>
                                          <td className="border-r border-slate-300 p-3 text-right break-words">{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                          <td className="p-3 text-right font-semibold break-words">{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td colSpan="11" className="p-6 text-center text-slate-500">No items found</td>
                                    </tr>
                                  )}
                                </tbody>
                              </>
                            )}
                          </table>
                        </div>
                      );
                    })()}

                  {/* Summary Section */}
                  <div className="p-6">
                    <div className="flex justify-between">
                      {/* Left Side - Bank Details */}
                      <div className="w-1/2">
                        <h3 className="font-semibold text-slate-900 mb-3">OUR BANK DETAILS</h3>
                        <div className="text-sm text-slate-600 space-y-1">
                          <div><strong>Bank Name:</strong> {companySettings?.bankName || 'Not Configured'}</div>
                          <div><strong>A/C No:</strong> {companySettings?.accountNumber || 'Not Configured'}</div>
                          <div><strong>IFSC:</strong> {companySettings?.ifscCode || 'Not Configured'}</div>
                          <div><strong>Branch:</strong> {companySettings?.branchName || 'Not Configured'}</div>
                        </div>

                        {/* Payment History */}
                        {selectedOrder.payments && selectedOrder.payments.length > 0 && (
                          <div className="mt-6">
                            <h4 className="font-semibold text-slate-900 mb-2">Payment History</h4>
                            <div className="text-sm text-slate-600 space-y-1">
                              {selectedOrder.payments.map((payment, index) => (
                                <div key={index} className="flex justify-between">
                                  <span>Payment #{index + 1} ({payment.paymentType || 'Cash'}):</span>
                                  <span>₹{(payment.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}


                      </div>

                      {/* Right Side - Totals */}
                      <div className="w-1/3">
                        <div className="space-y-3">
                          {(() => {
                            // Calculate subtotal (taxable amount without GST)
                            const subtotal = selectedOrder.items?.reduce((sum, item) => {
                              const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
                              const discountAmount = baseAmount * ((item.discount || 0) / 100);
                              return sum + (baseAmount - discountAmount);
                            }, 0) || 0;

                            return (
                              <GSTSummary
                                items={selectedOrder.items || []}
                                buyerGST={selectedOrder.customer_gst || selectedOrder.party_gst_no || ''}
                                companySettings={companySettings}
                                subtotal={subtotal}
                                amountReceived={selectedOrder.amountReceived || selectedOrder.amount_received || 0}
                                showOutstanding={true}
                              />
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    {selectedOrder.termsAndConditions && (
                      <div className="mt-8 pt-6 border-t border-slate-200">
                        <h4 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide">Terms and Conditions</h4>
                        <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                          {selectedOrder.termsAndConditions}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </div>
          </AppModal>
        )}

        {/* Enhanced Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          record={selectedOrder}
          onPaymentUpdate={handlePaymentSubmit}
          paymentHistory={selectedOrder?.payments || []}
          companySettings={companySettings}
        />

        {/* Convert Modal */}
        {showConvertModal && (
          <AppModal
            onClose={() => setShowConvertModal(false)}
            title="Convert Estimate to Closed Order"
            size="md"
            footer={
              <button
                onClick={() => setShowConvertModal(false)}
                className={modalButton.secondary}
              >
                Cancel
              </button>
            }
          >

              {/* Search Estimates */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search estimates by customer or number..."
                    value={estimateSearchTerm}
                    onChange={handleEstimateSearch}
                    className="pl-10 pr-4 py-3 w-full border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Estimates List */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Select an Estimate</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={fetchEstimates}
                      className="text-xs bg-blue-50 text-[#0A5F38] px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                      title="Refresh estimate list"
                    >
                      🔄 Refresh
                    </button>
                    <div className="text-xs text-slate-500 bg-white px-2 py-1 rounded">
                      Only showing unconverted estimates
                    </div>
                  </div>
                </div>
                {filteredEstimates.length === 0 ? (
                  <div className="text-center py-8 bg-white rounded-lg">
                    <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-600 font-medium">No unconverted estimates available</p>
                    <p className="text-slate-500 text-sm mt-1">All estimates have already been converted to closed orders or invoices</p>
                    <button
                      onClick={fetchEstimates}
                      className="mt-3 text-sm bg-blue-50 text-[#0A5F38] px-3 py-1 rounded hover:bg-blue-100 transition-colors"
                    >
                      🔄 Refresh List
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredEstimates.map(estimate => (
                      <div
                        key={estimate.id || estimate._id}
                        className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer transition-colors"
                        onClick={() => handleSelectEstimate(estimate)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-slate-900">
                              #{estimate.estimate_number || estimate._id?.slice(-8) || 'EST' + estimate.id}
                            </div>
                            <div className="text-sm text-slate-600">
                              Customer: {estimate.customer_name || 'Walk-in Customer'}
                            </div>
                            <div className="text-sm text-slate-600">
                              Date: {estimate.date ? new Date(estimate.date).toLocaleDateString('en-IN') : 'N/A'}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-slate-900">
                              {formatCurrency(estimate.totalAmount || estimate.total_amount)}
                            </div>
                            <div className="mt-2">
                              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Available for Conversion
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </AppModal>
        )}


        {/* Estimate Detail Modal for Conversion */}
        {showEstimateDetailModal && selectedEstimate && (
          <AppModal
            onClose={() => setShowEstimateDetailModal(false)}
            title="Convert to Closed Order"
            size="xl"
            footer={
              <>
                <button
                  onClick={() => setShowEstimateDetailModal(false)}
                  className={modalButton.secondary}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClosedOrderSubmit}
                  disabled={loading}
                  className={modalButton.primary}
                >
                  {loading ? 'Converting...' : 'Create Closed Order'}
                </button>
              </>
            }
          >

              {/* Estimate Summary */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Estimate Summary</h3>
                <div className="bg-white rounded-lg p-4 border-2 border-black grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-1">Estimate Number</div>
                    <div className="font-semibold">
                      #{selectedEstimate.estimate_number || selectedEstimate._id?.slice(-8) || 'EST' + selectedEstimate.id}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-1">Estimate Date</div>
                    <div className="font-semibold">
                      {selectedEstimate.date ? new Date(selectedEstimate.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-1">Total Amount</div>
                    <div className="font-semibold">
                      ₹{(selectedEstimate.totalAmount || selectedEstimate.total_amount || 0).toLocaleString()}
                    </div>
                  </div>
                  {selectedEstimate.salesperson_name && (
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Created By</div>
                      <div className="font-semibold">
                        {selectedEstimate.salesperson_name}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Closed Order Date</label>
                    <input
                      type="date"
                      value={closedOrderData.date || ''}
                      onChange={(e) => setClosedOrderData({ ...closedOrderData, date: e.target.value })}
                      className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm font-semibold"
                    />
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                  <button
                    onClick={() => {
                      if (!editMode) {
                        setEditingCustomer({
                          name: selectedEstimate.customer_name || '',
                          phone: selectedEstimate.customer_phone || '',
                          email: selectedEstimate.customer_email || '',
                          address: selectedEstimate.customer_address || ''
                        });
                      }
                      setEditMode(!editMode);
                    }}
                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                  >
                    {editMode ? 'Done' : 'Edit'}
                  </button>
                </div>
                {editMode ? (
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={editingCustomer.name || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <input
                        type="text"
                        value={editingCustomer.phone || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={editingCustomer.email || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                      <textarea
                        value={editingCustomer.address || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        rows="2"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                      <div className="font-semibold">{editingCustomer.name || selectedEstimate.customer_name || 'Walk-in Customer'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                      <div className="font-semibold">{editingCustomer.phone || selectedEstimate.customer_phone || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                      <div className="font-semibold">{editingCustomer.email || selectedEstimate.customer_email || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                      <div className="font-semibold">{editingCustomer.address || selectedEstimate.customer_address || 'N/A'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dispatch and Delivery Information */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 mt-6 shadow-sm">
                <h4 className="text-slate-900 font-bold mb-4 text-sm flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-[#0A5F38]" /> Dispatch & Delivery Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dispatched from</label>
                    <input
                      type="text"
                      value={closedOrderData.dispatched_from || ''}
                      onChange={(e) => setClosedOrderData({ ...closedOrderData, dispatched_from: e.target.value })}
                      placeholder="Enter dispatch location"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Delivered to</label>
                    <input
                      type="text"
                      value={closedOrderData.delivered_to || ''}
                      onChange={(e) => setClosedOrderData({ ...closedOrderData, delivered_to: e.target.value })}
                      placeholder="Enter delivery location"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                  <div className="space-x-2">
                    <button
                      onClick={async () => {
                        try {
                          console.log('🔄 Loading categories and products for Edit...');
                          // Use the utility function which has better error handling and fallback data
                          const formData = await getFormData();

                          console.log('📊 Loaded categories:', formData.categories?.length || 0);
                          console.log('📦 Loaded products:', formData.products?.length || 0);

                          // Update state with loaded data
                          setCategories((formData.categories || []).map(cat => ({
                            ...cat,
                            id: cat.id || cat._id,
                            isOthers: cat.isOthers || false
                          })));
                          setProducts(formData.products || []);

                          // Prepare items with proper IDs
                          const itemsWithIds = (selectedEstimate.items || []).map(item => ({
                            ...item,
                            category_id: item.category_id || '',
                            product_id: item.product_id || '',
                            hsn_code: item.hsn_code || item.productId?.product?.hsnCode || ''
                          }));

                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          // Check if any item has discount > 0 to set initial toggle state
                          const hasDiscount = itemsWithIds.some(item => (item.discount || 0) > 0);
                          setEditingApplyDiscount(hasDiscount);
                          setIsWoodWorkMode(!!(selectedEstimate.isWoodWork || selectedEstimate.is_wood_work));
                          setEditModalContext('estimateConvert');
                          setShowEditProductsModal(true);
                        } catch (error) {
                          console.error('Failed to load categories and products:', error);
                          toast.error('Failed to load product data');
                        }
                      }}
                      className="px-3 py-1 text-sm bg-blue-50 text-[#0A5F38] rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          console.log('🔄 Loading categories and products for Add Product...');
                          // Use the utility function which has better error handling and fallback data
                          const formData = await getFormData();

                          console.log('📊 Loaded categories:', formData.categories?.length || 0);
                          console.log('📦 Loaded products:', formData.products?.length || 0);

                          // Update state with loaded data
                          setCategories((formData.categories || []).map(cat => ({
                            ...cat,
                            id: cat.id || cat._id,
                            isOthers: cat.isOthers || false
                          })));
                          setProducts(formData.products || []);

                          // Prepare existing items with proper IDs
                          const itemsWithIds = (selectedEstimate.items || []).map(item => ({
                            ...item,
                            category_id: item.category_id || '',
                            product_id: item.product_id || '',
                            hsn_code: item.hsn_code || item.productId?.product?.hsnCode || ''
                          }));

                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          // Check if any item has discount > 0 to set initial toggle state
                          const hasDiscount = itemsWithIds.some(item => (item.discount || 0) > 0);
                          setEditingApplyDiscount(hasDiscount);
                          setIsWoodWorkMode(!!(selectedEstimate.isWoodWork || selectedEstimate.is_wood_work));
                          setEditModalContext('estimateConvert');
                          setShowEditProductsModal(true);
                        } catch (error) {
                          console.error('Failed to load categories and products:', error);
                          toast.error('Failed to load product data');
                        }
                      }}
                      className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      Add Product
                    </button>
                  </div>
                </div>
                <div className={modalTable.wrap}>
                  <table className={modalTable.table}>
                    <thead className={modalTable.thead}>
                      <tr>
                        <th className={modalTable.th}>Category</th>
                        <th className={modalTable.th}>Product</th>
                        <th className={modalTable.thCenter}>Units</th>
                        <th className={modalTable.thCenter}>Quantity</th>
                        <th className={modalTable.thRight}>Unit Price</th>
                        {/* Only show Discount column if any item has a discount */}
                        {(selectedEstimate.items && selectedEstimate.items.some(item => (item.discount || 0) > 0)) && (
                          <th className={modalTable.thCenter}>Disc %</th>
                        )}
                        <th className={modalTable.thRight}>Amount</th>
                        <th className={modalTable.thCenter}>GST %</th>
                        <th className={modalTable.thRight}>GST Amount</th>
                        <th className={modalTable.thRight}>Total</th>
                      </tr>
                    </thead>
                    <tbody className={modalTable.tbody}>
                      {selectedEstimate.items && selectedEstimate.items.map((item, index) => {
                        const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id));
                        const categoryName = category?.name || 'N/A';
                        const baseAmount = item.quantity * item.unit_price;
                        const discountAmount = baseAmount * ((item.discount || 0) / 100);
                        const taxableAmount = baseAmount - discountAmount;
                        const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                        const totalAmount = taxableAmount + gstAmount;

                        return (
                          <tr key={index} className={modalTable.tr}>
                            <td className={`${modalTable.td} whitespace-nowrap`}>{categoryName}</td>
                            <td className={`${modalTable.td} font-medium`}>{item.product_name}</td>
                            <td className={modalTable.tdCenter}>{item.unit_type || 'SFT'}</td>
                            <td className={modalTable.tdCenter}>{item.quantity}</td>
                            <td className={modalTable.tdRight}>₹{parseFloat(item.unit_price).toLocaleString()}</td>
                            {(selectedEstimate.items && selectedEstimate.items.some(i => (i.discount || 0) > 0)) && (
                              <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                            )}
                            <td className={modalTable.tdRight}>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className={modalTable.tdCenter}>{item.gst_rate || 0}%</td>
                            <td className={modalTable.tdRight}>₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {selectedEstimate.items && selectedEstimate.items.some(i => (i.discount || 0) > 0) ? (
                      <tfoot className={modalTable.tfoot}>
                        <tr>
                          <td colSpan="9" className="px-3 py-2.5 text-right font-semibold text-slate-700">Total:</td>
                          <td className="px-3 py-2.5 text-right font-bold text-slate-900 whitespace-nowrap">
                            ₹{(selectedEstimate.items && selectedEstimate.items.length > 0
                              ? calculateInvoiceTotal(selectedEstimate.items)
                              : 0
                            ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    ) : (
                      <tfoot className={modalTable.tfoot}>
                        <tr>
                          <td colSpan="8" className="px-3 py-2.5 text-right font-semibold text-slate-700">Total:</td>
                          <td className="px-3 py-2.5 text-right font-bold text-slate-900 whitespace-nowrap">
                            ₹{(selectedEstimate.items && selectedEstimate.items.length > 0
                              ? calculateInvoiceTotal(selectedEstimate.items)
                              : 0
                            ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* GST Number Field */}
              <div className="mb-6">
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <label className="block text-sm font-medium text-slate-700 mb-2">GST Number </label>
                  <input
                    type="text"
                    value={closedOrderData.gstNumber || ''}
                    onChange={(e) => setClosedOrderData({ ...closedOrderData, gstNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter 15-digit GST number"
                    maxLength="15"
                  />
                  {closedOrderData.gstNumber && closedOrderData.gstNumber.length > 0 && closedOrderData.gstNumber.length < 15 && (
                    <p className="text-orange-500 text-sm mt-1">GST number should be 15 characters long if provided</p>
                  )}
                </div>
              </div>

              {/* GST Breakdown */}
              <div className="mb-6 p-3 bg-white rounded-lg">
                <GSTBreakdown
                  items={selectedEstimate?.items || []}
                  buyerGST={closedOrderData.gstNumber}
                  companySettings={companySettings}
                  showTitle={true}
                  className="text-sm"
                />
              </div>

              {/* Payment & Invoice Details */}
              <div className="bg-[#FDFCEB] p-5 rounded-xl border border-[#0A5F38]/20 shadow-sm space-y-6">
                <h3 className="text-lg font-bold text-[#0A5F38] flex items-center">
                  <Calculator className="w-5 h-5 mr-2" />
                  Payment & Invoice Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Card: Payment Summary */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-[#0A5F38] font-bold mb-4 flex items-center text-sm">
                      <DollarSign className="w-4 h-4 mr-2" /> Payment Summary
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Total Invoice Amount:</span>
                        <span className="font-semibold text-slate-900">
                          ₹{calculateInvoiceTotal(selectedEstimate.items).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#16a34a] font-medium">Previously Paid:</span>
                        <span className="text-[#16a34a]">
                          - ₹0.00
                        </span>
                      </div>
                      <div className="border-t border-slate-100 pt-3 flex justify-between text-sm">
                        <span className="font-bold text-slate-900">Remaining Balance:</span>
                        <span className="font-bold text-slate-900">
                          ₹{calculateInvoiceTotal(selectedEstimate.items).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between text-base pt-1">
                        <span className="font-bold text-slate-900">Final Balance:</span>
                        <span className="font-bold text-[#dc2626]">
                          ₹{(() => {
                            const total = calculateInvoiceTotal(selectedEstimate.items);
                            const amountReceivedNow = parseFloat(closedOrderData.amountReceived) || 0;
                            const finalBalance = Math.max(0, total - amountReceivedNow);
                            return finalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 });
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Card: New Payment Entry */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-slate-900 font-bold mb-4 text-sm">New Payment Entry</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Mode</label>
                        <select
                          value={closedOrderData.paymentType}
                          onChange={(e) => setClosedOrderData({ ...closedOrderData, paymentType: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                        >
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="phonepe">PhonePe</option>
                          <option value="google_pay">Google Pay</option>
                          <option value="paytm">Paytm</option>
                          <option value="net_banking">Net Banking</option>
                          <option value="card">Card</option>
                          <option value="cheque">Cheque</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Amount</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-slate-400 text-sm">₹</span>
                          <input
                            type="number"
                            value={closedOrderData.amountReceived}
                            onChange={(e) => {
                              const amount = parseFloat(e.target.value) || 0;
                              const totalAmount = calculateInvoiceTotal(selectedEstimate.items);

                              // Validation: Cannot exceed total amount
                              if (amount > totalAmount) {
                                toast.error(`Amount cannot exceed the total order amount (₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })})`);
                                return;
                              }

                              // Auto-calculate payment status
                              let status = 'pending';
                              if (amount <= 0) {
                                status = 'pending';
                              } else if (Math.abs(amount - totalAmount) < 0.01) {
                                status = 'paid';
                              } else {
                                status = 'partial';
                              }

                              setClosedOrderData({
                                ...closedOrderData,
                                amountReceived: e.target.value,
                                paymentStatus: status
                              });
                            }}
                            onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                            onWheel={(e) => e.target.blur()}
                            className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="Enter payment amount"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div className="pt-1">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Status</label>
                        <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${closedOrderData.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                          closedOrderData.paymentStatus === 'partial' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                          {closedOrderData.paymentStatus === 'paid' ? 'Paid' :
                            closedOrderData.paymentStatus === 'partial' ? 'Partial Payment' :
                              'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mt-6">
                <TermsAndConditionsInput
                  value={closedOrderData.termsAndConditions || ''}
                  onChange={(value) => setClosedOrderData({ ...closedOrderData, termsAndConditions: value })}
                />
              </div>
          </AppModal>
        )}

        {/* Convert Closed Order to Invoice Modal */}
        {showConvertToInvoiceModal && convertingClosedOrder && (
          <AppModal
            onClose={() => setShowConvertToInvoiceModal(false)}
            title="Convert to Invoice"
            size="xl"
            footer={
              <>
                <button
                  onClick={() => setShowConvertToInvoiceModal(false)}
                  className={modalButton.secondary}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateInvoiceFromModal}
                  disabled={loading || (conversionPaymentData.gst_number && conversionPaymentData.gst_number.length > 0 && conversionPaymentData.gst_number.length < 15)}
                  className={modalButton.primary}
                >
                  {loading ? 'Converting...' : 'Create Invoice'}
                </button>
              </>
            }
          >

              {/* Closed Order Summary */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Closed Order Summary</h3>
                <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-1">Closed Order Number</div>
                    <div className="font-semibold">
                      {convertingClosedOrder.closed_order_number || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-1">Closed Order Date</div>
                    <div className="font-semibold">
                      {convertingClosedOrder.date ? new Date(convertingClosedOrder.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-1">Total Amount</div>
                    <div className="font-semibold">
                      {formatCurrency(convertingClosedOrder.totalAmount || convertingClosedOrder.total_amount)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
                    <input
                      type="date"
                      value={conversionPaymentData.date || ''}
                      onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, date: e.target.value })}
                      className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm font-semibold"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Invoice Information</h3>
                <div className="bg-white rounded-lg p-4 border-2 border-black">
                  <div className="max-w-md">
                    <InvoiceNumberField
                      value={invoiceNumber || ''}
                      onChange={setInvoiceNumber}
                      label="Invoice Number"
                      placeholder="Enter invoice number"
                    />
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                  <button
                    onClick={() => {
                      if (!editMode) {
                        setConversionEditingCustomer({
                          name: convertingClosedOrder.customer_name || '',
                          phone: convertingClosedOrder.customer_phone || '',
                          email: convertingClosedOrder.customer_email || '',
                          address: convertingClosedOrder.customer_address || ''
                        });
                      }
                      setEditMode(!editMode);
                    }}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                  >
                    {editMode ? 'Done' : 'Edit'}
                  </button>
                </div>
                {editMode ? (
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={conversionEditingCustomer.name || ''}
                        onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <input
                        type="text"
                        value={conversionEditingCustomer.phone || ''}
                        onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={conversionEditingCustomer.email || ''}
                        onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, email: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                      <textarea
                        value={conversionEditingCustomer.address || ''}
                        onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, address: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        rows="2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                      <ValidatedSelect
                        value={conversionEditingCustomer.state || ''}
                        onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, state: e.target.value })}
                        options={[
                          { value: '', label: 'Select State' },
                          ...stateOptions
                        ]}
                        placeholder="Select State"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                      <input
                        type="text"
                        value={conversionPaymentData.gst_number || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setConversionPaymentData(prev => ({ ...prev, gst_number: val }));
                          if (val.length >= 2) {
                            const stateName = getStateNameFromGST(val);
                            if (stateName) {
                              setConversionEditingCustomer(prev => ({ ...prev, state: stateName }));
                            }
                          }
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        placeholder="Enter GST Number"
                        maxLength="15"
                      />
                      {conversionPaymentData.gst_number && conversionPaymentData.gst_number.length > 0 && conversionPaymentData.gst_number.length < 15 && (
                        <div className="text-orange-500 text-sm mt-1">
                          GST number should be 15 characters long if provided
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                      <div className="font-semibold">{conversionEditingCustomer.name || convertingClosedOrder.customer_name || 'Walk-in Customer'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                      <div className="font-semibold">{conversionEditingCustomer.phone || convertingClosedOrder.customer_phone || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                      <div className="font-semibold">{conversionEditingCustomer.email || convertingClosedOrder.customer_email || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                      <div className="font-semibold">{conversionEditingCustomer.address || convertingClosedOrder.customer_address || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                      <div className="font-semibold">{conversionEditingCustomer.state || convertingClosedOrder.customer_state || convertingClosedOrder.party_state_name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">GST Number</div>
                      <div className="font-semibold">{conversionPaymentData.gst_number || 'N/A'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                  <div className="space-x-2">
                    <button
                      onClick={async () => {
                        if (!categories.length || !products.length) {
                          const data = await getFormData();
                          setCategories((data.categories || []).map(cat => ({
                            ...cat,
                            id: cat.id || cat._id,
                            isOthers: cat.isOthers || false
                          })));
                          setProducts(data.products || []);
                        }
                        setEditModalContext('invoiceConvert');
                        const itemsWithIds = (convertingClosedOrder.items || []).map(item => ({
                          ...item,
                          category_id: item.category_id || '',
                          product_id: item.product_id || '',
                          hsn_code: item.hsn_code || item.productId?.product?.hsnCode || ''
                        }));
                        setEditingProducts(recalcItemsAmounts(itemsWithIds));
                        setIsWoodWorkMode(!!(convertingClosedOrder.isWoodWork || convertingClosedOrder.is_wood_work));
                        setShowEditProductsModal(true);
                      }}
                      className="px-3 py-1 text-sm bg-[#E67E22] text-white rounded-lg hover:bg-[#d5731e] transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!categories.length || !products.length) {
                          const data = await getFormData();
                          setCategories((data.categories || []).map(cat => ({
                            ...cat,
                            id: cat.id || cat._id,
                            isOthers: cat.isOthers || false
                          })));
                          setProducts(data.products || []);
                        }
                        setEditModalContext('invoiceConvert');
                        setIsWoodWorkMode(!!(convertingClosedOrder.isWoodWork || convertingClosedOrder.is_wood_work));
                        setShowEditProductsModal(true);
                        const itemsWithIds = (convertingClosedOrder.items || []).map(item => ({
                          ...item,
                          category_id: item.category_id || '',
                          product_id: item.product_id || '',
                          hsn_code: item.hsn_code || item.productId?.product?.hsnCode || ''
                        }));
                        setEditingProducts(recalcItemsAmounts(itemsWithIds));
                      }}
                      className="px-3 py-1 text-sm bg-[#005C2D] text-white rounded-lg hover:bg-[#004722] transition-colors"
                    >
                      Add Product
                    </button>
                  </div>
                </div>
                <div className={modalTable.wrap}>
                  <table className={modalTable.table}>
                    <thead className={modalTable.thead}>
                      <tr>
                        <th className={modalTable.th}>Category</th>
                        <th className={modalTable.th}>Product</th>
                        <th className={modalTable.thCenter}>Units</th>
                        <th className={modalTable.thCenter}>Quantity</th>
                        <th className={modalTable.thRight}>Unit Price</th>
                        {/* Only show Discount column if any item has a discount */}
                        {(convertingClosedOrder.items && convertingClosedOrder.items.some(item => (item.discount || 0) > 0)) && (
                          <th className={modalTable.thCenter}>Disc %</th>
                        )}
                        <th className={modalTable.thRight}>Amount</th>
                        <th className={modalTable.thCenter}>GST %</th>
                        <th className={modalTable.thRight}>GST Amount</th>
                        <th className={modalTable.thRight}>Total</th>
                      </tr>
                    </thead>
                    <tbody className={modalTable.tbody}>
                      {convertingClosedOrder.items && convertingClosedOrder.items.map((item, index) => {
                        const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id));
                        const categoryName = category?.name || 'N/A';
                        const baseAmount = item.quantity * item.unit_price;
                        const discountAmount = baseAmount * ((item.discount || 0) / 100);
                        const taxableAmount = baseAmount - discountAmount;
                        const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                        const totalAmount = taxableAmount + gstAmount;

                        return (
                          <tr key={index} className={modalTable.tr}>
                            <td className={`${modalTable.td} whitespace-nowrap`}>{categoryName}</td>
                            <td className={`${modalTable.td} font-medium`}>{item.product_name}</td>
                            <td className={modalTable.tdCenter}>{item.unit_type || 'SFT'}</td>
                            <td className={modalTable.tdCenter}>{item.quantity}</td>
                            <td className={modalTable.tdRight}>₹{parseFloat(item.unit_price).toLocaleString()}</td>
                            {(convertingClosedOrder.items && convertingClosedOrder.items.some(i => (i.discount || 0) > 0)) && (
                              <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                            )}
                            <td className={modalTable.tdRight}>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className={modalTable.tdCenter}>{item.gst_rate || 0}%</td>
                            <td className={modalTable.tdRight}>₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {convertingClosedOrder.items && convertingClosedOrder.items.some(i => (i.discount || 0) > 0) ? (
                      <tfoot className={modalTable.tfoot}>
                        <tr>
                          <td colSpan="9" className="px-3 py-2.5 text-right font-semibold text-slate-700">Total:</td>
                          <td className="px-3 py-2.5 text-right font-bold text-slate-900 whitespace-nowrap">
                            ₹{(convertingClosedOrder.items && convertingClosedOrder.items.length > 0
                              ? convertingClosedOrder.items.reduce((sum, item) => {
                                const baseAmount = item.quantity * item.unit_price;
                                const discountAmount = baseAmount * ((item.discount || 0) / 100);
                                const taxableAmount = baseAmount - discountAmount;
                                const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                                return sum + (taxableAmount + gstAmount);
                              }, 0)
                              : 0
                            ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    ) : (
                      <tfoot className={modalTable.tfoot}>
                        <tr>
                          <td colSpan="8" className="px-3 py-2.5 text-right font-semibold text-slate-700">Total:</td>
                          <td className="px-3 py-2.5 text-right font-bold text-slate-900 whitespace-nowrap">
                            ₹{(convertingClosedOrder.items && convertingClosedOrder.items.length > 0
                              ? convertingClosedOrder.items.reduce((sum, item) => {
                                const baseAmount = item.quantity * item.unit_price;
                                const discountAmount = baseAmount * ((item.discount || 0) / 100);
                                const taxableAmount = baseAmount - discountAmount;
                                const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                                return sum + (taxableAmount + gstAmount);
                              }, 0)
                              : 0
                            ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {/* GST Breakdown */}
                <div className="mt-4 p-3 bg-white rounded-lg">
                  <GSTBreakdown
                    items={convertingClosedOrder?.items || []}
                    buyerGST={conversionPaymentData.gst_number}
                    companySettings={companySettings}
                    showTitle={true}
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Payment & Invoice Details */}
              <div className="bg-[#FDFCEB] p-5 rounded-xl border border-[#0A5F38]/20 shadow-sm space-y-6">
                <h3 className="text-lg font-bold text-[#0A5F38] flex items-center">
                  <Calculator className="w-5 h-5 mr-2" />
                  Payment Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Card: Payment Summary */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-[#0A5F38] font-bold mb-4 flex items-center text-sm">
                      <DollarSign className="w-4 h-4 mr-2" /> Payment Summary
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Total Invoice Amount:</span>
                        <span className="font-semibold text-slate-900">
                          ₹{(convertingClosedOrder.items || []).reduce((sum, item) => {
                            const baseAmount = item.quantity * item.unit_price;
                            const discountAmount = baseAmount * ((item.discount || 0) / 100);
                            const taxableAmount = baseAmount - discountAmount;
                            const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                            return sum + (taxableAmount + gstAmount);
                          }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#16a34a] font-medium">Previously Paid:</span>
                        <span className="text-[#16a34a]">
                          - ₹{(convertingClosedOrder.amountReceived || convertingClosedOrder.amount_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="border-t border-slate-100 pt-3 flex justify-between text-sm">
                        <span className="font-bold text-slate-900">Remaining Balance:</span>
                        <span className="font-bold text-slate-900">
                          ₹{(() => {
                            const total = (convertingClosedOrder.items || []).reduce((sum, item) => {
                              const baseAmount = item.quantity * item.unit_price;
                              const discountAmount = baseAmount * ((item.discount || 0) / 100);
                              const taxableAmount = baseAmount - discountAmount;
                              const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                              return sum + (taxableAmount + gstAmount);
                            }, 0);
                            const paid = convertingClosedOrder.amountReceived || convertingClosedOrder.amount_received || 0;
                            return Math.max(0, total - paid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between text-base pt-1">
                        <span className="font-bold text-slate-900">Final Balance:</span>
                        <span className="font-bold text-[#dc2626]">
                          ₹{(() => {
                            const total = (convertingClosedOrder.items || []).reduce((sum, item) => {
                              const baseAmount = item.quantity * item.unit_price;
                              const discountAmount = baseAmount * ((item.discount || 0) / 100);
                              const taxableAmount = baseAmount - discountAmount;
                              const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                              return sum + (taxableAmount + gstAmount);
                            }, 0);
                            const previouslyPaid = convertingClosedOrder.amountReceived || convertingClosedOrder.amount_received || 0;
                            const amountReceivedNow = parseFloat(conversionPaymentData.amount_received) || 0;
                            const finalBalance = Math.max(0, total - previouslyPaid - amountReceivedNow);
                            return finalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Card: New Payment Entry */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-slate-900 font-bold mb-4 text-sm">New Payment Entry</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Mode</label>
                        <select
                          value={conversionPaymentData.payment_type}
                          onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, payment_type: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                        >
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="phonepe">PhonePe</option>
                          <option value="google_pay">Google Pay</option>
                          <option value="paytm">Paytm</option>
                          <option value="net_banking">Net Banking</option>
                          <option value="card">Card</option>
                          <option value="cheque">Cheque</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Amount</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-slate-400 text-sm">₹</span>
                          <input
                            type="number"
                            value={conversionPaymentData.amount_received}
                            onChange={(e) => {
                              const amount = parseFloat(e.target.value) || 0;
                              const totalAmount = (convertingClosedOrder.items || []).reduce((sum, item) => {
                                const baseAmount = item.quantity * item.unit_price;
                                const discountAmount = baseAmount * ((item.discount || 0) / 100);
                                const taxableAmount = baseAmount - discountAmount;
                                const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                                return sum + (taxableAmount + gstAmount);
                              }, 0);
                              const previouslyPaid = convertingClosedOrder.amountReceived || convertingClosedOrder.amount_received || 0;

                              // Auto-calculate payment status
                              let status = 'pending';
                              const cumulative = previouslyPaid + amount;

                              if (cumulative <= 0) {
                                status = 'pending';
                              } else if (Math.abs(cumulative - totalAmount) < 0.01 || cumulative > totalAmount) {
                                status = 'completed';
                              } else {
                                status = 'partial';
                              }

                              setConversionPaymentData({
                                ...conversionPaymentData,
                                amount_received: e.target.value,
                                payment_status: status
                              });
                            }}
                            onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                            onWheel={(e) => e.target.blur()}
                            className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="Enter payment amount"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 italic mt-1.5 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Leave blank or enter 0 if no additional payment is received at this time.
                        </p>
                      </div>

                      <div className="pt-1">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Status</label>
                        <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${conversionPaymentData.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                          conversionPaymentData.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                          {conversionPaymentData.payment_status === 'completed' ? 'Paid' :
                            conversionPaymentData.payment_status === 'partial' ? 'Partial Payment' :
                              'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dispatch and Delivery Information */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 mt-6 shadow-sm">
                  <h4 className="text-slate-900 font-bold mb-4 text-sm flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-[#0A5F38]" /> Dispatch & Delivery Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dispatched from</label>
                      <input
                        type="text"
                        value={conversionPaymentData.dispatched_from}
                        onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, dispatched_from: e.target.value })}
                        placeholder="Enter dispatch location"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Delivered to</label>
                      <input
                        type="text"
                        value={conversionPaymentData.delivered_to}
                        onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, delivered_to: e.target.value })}
                        placeholder="Enter delivery location"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <div>
                    <h4 className="text-sm font-bold text-[#0A5F38]">Include Terms & Conditions</h4>
                    <p className="text-xs text-slate-500">Enable if you wish to add terms and conditions to this invoice</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const nextState = !includeTermsClosedOrder;
                      setIncludeTermsClosedOrder(nextState);
                      if (nextState) {
                        setConversionPaymentData(prev => ({ ...prev, termsAndConditions: convertingClosedOrder?.termsAndConditions || '' }));
                      } else {
                        setConversionPaymentData(prev => ({ ...prev, termsAndConditions: '' }));
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      includeTermsClosedOrder ? 'bg-[#0A5F38]' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        includeTermsClosedOrder ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {includeTermsClosedOrder && (
                  <TermsAndConditionsInput
                    value={conversionPaymentData.termsAndConditions || ''}
                    onChange={(value) => setConversionPaymentData({ ...conversionPaymentData, termsAndConditions: value })}
                  />
                )}
              </div>
          </AppModal>
        )}



        {/* Cancellation Modal */}
        <CancellationModal
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
          onConfirm={handleConfirmCancellation}
          title="Cancel Closed Order"
          itemType="closed_order"
          itemNumber={selectedOrder?.closed_order_number || selectedOrder?.order_number}
          loading={cancellationLoading}
          currentPaymentStatus={selectedOrder?.paymentStatus || selectedOrder?.payment_status}
        />

        {/* Payment Status Update Modal for Cancelled Orders */}
        {showPaymentStatusUpdateModal && (
          <AppModal
            onClose={() => {
              setShowPaymentStatusUpdateModal(false);
              setPaymentStatusUpdate('');
            }}
            title="Update Payment Status"
            size="sm"
            layer="top"
            footer={
              <>
                <button
                  onClick={() => {
                    setShowPaymentStatusUpdateModal(false);
                    setPaymentStatusUpdate('');
                  }}
                  disabled={paymentStatusUpdateLoading}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50"
                >
                  Close
                </button>
                <button
                  onClick={handleUpdatePaymentStatus}
                  disabled={paymentStatusUpdateLoading || !paymentStatusUpdate}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {paymentStatusUpdateLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Status'
                  )}
                </button>
              </>
            }
          >

              <div>
                <p className="text-slate-700 mb-4">
                  Update the payment status for this cancelled closed order.
                </p>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    value={paymentStatusUpdate}
                    onChange={(e) => setPaymentStatusUpdate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select payment status...</option>
                    <option value="refund_initiated">Refund Initiated – Refund process started</option>
                    <option value="refund_completed">Refund Completed – Refund processed successfully</option>
                    <option value="no_payment">Cancelled (No Payment) – No refund needed</option>
                  </select>
                </div>

                {selectedOrder?.cancellation_payment_status && (
                  <div className="mt-4 p-3 bg-white rounded-lg">
                    <p className="text-sm text-slate-600">
                      <strong>Current Status:</strong> {
                        selectedOrder.cancellation_payment_status === 'refund_initiated' && 'Refund Initiated – Refund process started'
                      }
                      {selectedOrder.cancellation_payment_status === 'refund_completed' && 'Refund Completed – Refund processed successfully'}
                      {selectedOrder.cancellation_payment_status === 'no_payment' && 'Cancelled (No Payment) – No refund needed'}
                    </p>
                  </div>
                )}
              </div>
          </AppModal>
        )}

        {/* Edit Closed Order Modal */}
        {showEditClosedOrderModal && editingOrder && (
          <AppModal
            onClose={() => setShowEditClosedOrderModal(false)}
            title="Edit Closed Order"
            size="lg"
            footer={
              <>
                <button
                  onClick={() => setShowEditClosedOrderModal(false)}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrderEdits}
                  disabled={loading}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </>
            }
          >

              {/* Order Summary */}
              <div className="mb-6 p-4 bg-white rounded-lg border-2 border-black shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide">Order Summary</h3>
                  <button
                    onClick={() => setEditSummaryMode(!editSummaryMode)}
                    className="text-[#0A5F38] hover:text-[#084B2C] text-sm font-bold flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    {editSummaryMode ? 'Done' : 'Edit'}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-bold text-[#0A5F38] mb-1">Order Number</div>
                    <div className="font-semibold text-gray-800">
                      {editingOrder.closed_order_number || editingOrder.closedOrderNumber || editingOrder._id?.slice(-8) || 'CO' + editingOrder.id}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#0A5F38] mb-1">Date</div>
                    {editSummaryMode ? (
                      <input
                        type="date"
                        value={editingOrder.date ? new Date(editingOrder.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                        onChange={(e) => setEditingOrder({ ...editingOrder, date: e.target.value })}
                        className="w-full px-3 py-2 border-2 border-[#C5A57D] rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm bg-white"
                      />
                    ) : (
                      <div className="font-semibold text-gray-800">
                        {editingOrder.date ? new Date(editingOrder.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#0A5F38] mb-1">Created By</div>
                    <div className="font-semibold text-gray-800">
                      {editingOrder.salesperson?.name || editingOrder.salesperson_name || (user && user.role === 'admin' ? user.name : 'N/A')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                  <button
                    onClick={() => {
                      if (!editMode) {
                        setEditingCustomer({
                          name: editingOrder.customer_name || editingOrder.customer?.name || '',
                          phone: editingOrder.customer_phone || editingOrder.customer?.phone || '',
                          email: editingOrder.customer_email || editingOrder.customer?.email || '',
                          address: editingOrder.customer_address || editingOrder.customer?.address || ''
                        });
                      }
                      setEditMode(!editMode);
                    }}
                    className="px-4 py-2 text-sm bg-[#0A5F38] text-white rounded-lg hover:bg-[#0d7a4a] transition-colors font-medium"
                  >
                    {editMode ? 'Done' : 'Edit'}
                  </button>
                </div>
                {editMode ? (
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={editingCustomer.name || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <input
                        type="text"
                        value={editingCustomer.phone || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={editingCustomer.email || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                      <input
                        type="text"
                        value={editingCustomer.gstNumber || ''}
                        onChange={(e) => handleGSTFieldChange(e.target.value,
                          (gstValue) => setEditingCustomer({ ...editingCustomer, gstNumber: gstValue }),
                          (stateName) => setEditingCustomer(prev => ({ ...prev, state: stateName }))
                        )}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        placeholder="e.g. 29ABCDE1234F1Z5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                      <select
                        value={editingCustomer.state || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, state: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      >
                        <option value="">Select State</option>
                        {stateOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                      <textarea
                        value={editingCustomer.address || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        rows="2"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                      <div className="font-semibold">{editingCustomer.name || editingOrder.customer_name || 'Walk-in Customer'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                      <div className="font-semibold">{editingCustomer.phone || editingOrder.customer_phone || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                      <div className="font-semibold">{editingCustomer.email || editingOrder.customer_email || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                      <div className="font-semibold">{editingCustomer.address || editingOrder.customer_address || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                      <div className="font-semibold">{editingCustomer.state || editingOrder.customer_state || editingOrder.party_state_name || 'N/A'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dispatch and Delivery Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Dispatch & Delivery Information</h3>
                <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Dispatched from</label>
                    <input
                      type="text"
                      value={editingOrder.dispatched_from || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, dispatched_from: e.target.value })}
                      placeholder="Enter dispatch location"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Delivered to</label>
                    <input
                      type="text"
                      value={editingOrder.delivered_to || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, delivered_to: e.target.value })}
                      placeholder="Enter delivery location"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                  {user && user.role === 'admin' &&
                    editingOrder?.cancellation_status !== 'cancelled' &&
                    editingOrder?.status !== 'cancelled' &&
                    !editingOrder?.is_converted &&
                    !editingOrder?.converted_to_invoice_id &&
                    !editingOrder?.invoice_number &&
                    editingOrder?.conversion_status !== 'converted_to_invoice' && (
                      <button
                        onClick={async () => {
                          // Fetch data locally to avoid state staleness issues during mapping
                          let localCategories = categories;
                          let localProducts = products;

                          if (!localCategories.length || !localProducts.length) {
                            try {
                              const data = await getFormData();
                              localCategories = data.categories || [];
                              localProducts = data.products || [];
                              setCategories(localCategories);
                              setProducts(localProducts);
                            } catch (err) {
                              console.error("Failed to load products/categories", err);
                            }
                          }

                          setEditModalContext('edit');

                          // ALWAYS re-initialize editing products to avoid stale data from previous edits
                          const itemsToMap = (editingOrder ? editingOrder.items : []) || [];
                          const itemsWithIds = recalcItemsAmounts(mapItemsForEditing(itemsToMap, localCategories, localProducts));

                          console.log('✨ Mapped items for edit modal:', itemsWithIds);
                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));

                          setIsWoodWorkMode(!!(editingOrder.isWoodWork || editingOrder.is_wood_work));
                          setShowEditProductsModal(true);
                        }}
                        className="px-4 py-2 text-sm bg-[#0A5F38] text-white rounded-lg hover:bg-[#0d7a4a] transition-colors font-medium"
                      >
                        Edit Products
                      </button>
                    )}
                </div>
                <div className={modalTable.wrap}>
                  <table className={modalTable.table}>
                    <thead className={modalTable.thead}>
                      <tr>
                        <th className={modalTable.th}>Category</th>
                        <th className={modalTable.th}>Product</th>
                        <th className={modalTable.thCenter}>Units</th>
                        <th className={modalTable.thCenter}>Quantity</th>
                        <th className={modalTable.thRight}>Unit Price</th>
                        {(editingProducts.length > 0 ? editingProducts : editingOrder.items || []).some(i => (i.discount || 0) > 0) && (
                          <th className={modalTable.thCenter}>Disc %</th>
                        )}
                        <th className={modalTable.thRight}>Amount</th>
                        <th className={modalTable.thCenter}>GST %</th>
                        <th className={modalTable.thRight}>Total</th>
                      </tr>
                    </thead>
                    <tbody className={modalTable.tbody}>
                      {(editingProducts.length > 0 ? editingProducts : editingOrder.items || []).map((item, index) => {
                        const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id));
                        const categoryName = category?.name || 'N/A';
                        const baseAmount = item.quantity * item.unit_price;
                        const discountAmount = baseAmount * ((item.discount || 0) / 100);
                        const taxableAmount = baseAmount - discountAmount;
                        const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                        const totalAmount = taxableAmount + gstAmount;
                        return (
                          <tr key={index} className={modalTable.tr}>
                            <td className={`${modalTable.td} whitespace-nowrap`}>{categoryName}</td>
                            <td className={modalTable.td}>
                              <div className="font-medium text-slate-900 whitespace-pre-wrap">{item.product_name}</div>
                            </td>
                            <td className={modalTable.tdCenter}>{item.unit_type || 'SFT'}</td>
                            <td className={modalTable.tdCenter}>{item.quantity}</td>
                            <td className={modalTable.tdRight}>₹{parseFloat(item.unit_price).toLocaleString()}</td>
                            {(editingProducts.length > 0 ? editingProducts : editingOrder.items || []).some(i => (i.discount || 0) > 0) && (
                              <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                            )}
                            <td className={modalTable.tdRight}>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className={`${modalTable.tdCenter} font-medium`}>{item.gst_rate || 0}%</td>
                            <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Total Summary */}
              <div className="mb-6 bg-white rounded-lg p-4">
                <div className="flex justify-end">
                  <div className="w-64">
                    <div className="flex justify-between py-2 text-lg font-bold border-t border-slate-300">
                      <span>Grand Total:</span>
                      <span>₹{(editingProducts.length > 0 ? editingProducts : editingOrder.items || []).reduce((sum, item) => {
                        const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
                        const discountAmount = baseAmount * ((item.discount || 0) / 100);
                        const taxableAmount = baseAmount - discountAmount;
                        const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                        return sum + taxableAmount + gstAmount;
                      }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mb-6">
                <TermsAndConditionsInput
                  value={editingOrder.termsAndConditions || ''}
                  onChange={(value) => setEditingOrder({ ...editingOrder, termsAndConditions: value })}
                />
              </div>
          </AppModal>
        )}

        {/* Edit Products Modal - Standardized Implementation */}
        {showEditProductsModal && (
          <AppModal
            onClose={closeEditProductsModal}
            title="Edit Products"
            size="full"
            layer="stacked"
            footer={
              <>
                {!isWoodWorkMode && editingProducts.length > 0 && (
                  <div className="flex-1 min-w-[200px]">
                    <span className="text-sm font-medium text-slate-600 mr-2">Grand Total:</span>
                    <span className="text-xl font-black text-[#0A5F38]">
                      ₹{editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <button
                  onClick={closeEditProductsModal}
                  className={modalButton.secondary}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProductEdits}
                  className={modalButton.primary}
                >
                  Save Changes
                </button>
              </>
            }
          >

              {/* Apply Discount Toggle - Hide for Wood Work */}
              {!isWoodWorkMode && (
                <div className="mb-4 flex items-center justify-end">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-slate-700">Apply Discount</span>
                    <button
                      onClick={() => {
                        const newDiscountState = !editingApplyDiscount;
                        setEditingApplyDiscount(newDiscountState);
                        // If turning off, reset all discounts to 0
                        if (!newDiscountState) {
                          const updatedProducts = editingProducts.map(item => ({
                            ...item,
                            discount: 0
                          }));
                          setEditingProducts(updatedProducts);
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${editingApplyDiscount ? 'bg-[#C5A57D]' : 'bg-slate-200'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editingApplyDiscount ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-6">
                {isWoodWorkMode ? (
                  /* ══ WOOD WORK PREMIUM CONTINUOUS TABLE EDIT LAYOUT ══ */
                  <div className={modalTable.wrap}>
                    <table className={modalTable.table}>
                      <thead className={modalTable.thead}>
                        <tr>
                          <th className={modalTable.thCenter} style={{ width: '50px' }}>SI</th>
                          <th className={modalTable.th}>Type</th>
                          <th className={modalTable.thCenter} style={{ width: '100px' }}>Rate (₹)</th>
                          <th className={modalTable.thCenter} style={{ width: '60px' }}>Nos</th>
                          <th className={modalTable.thCenter} style={{ width: '70px' }}>L</th>
                          <th className={modalTable.thCenter} style={{ width: '70px' }}>B</th>
                          <th className={modalTable.thCenter} style={{ width: '80px' }}>Qty</th>
                          <th className={modalTable.thCenter} style={{ width: '80px' }}>GST %</th>
                          <th className={modalTable.thRight} style={{ width: '120px' }}>Amount (₹)</th>
                          <th className={modalTable.thCenter} style={{ width: '50px' }}>Act</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                          const categoryOrder = [];
                          editingProducts.forEach(item => {
                            const cat = item.categoryLabel || 'Section';
                            if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                          });

                          const rows = [];
                          categoryOrder.forEach((catLabel, catIdx) => {
                            const catEntries = editingProducts
                              .map((item, idx) => ({ item, idx }))
                              .filter(({ item }) => (item.categoryLabel || 'Section') === catLabel);

                            // Category Header Row
                            rows.push(
                              <tr key={'edit-cat-header-' + catLabel} className="bg-[#BDD7EE] border-b border-black">
                                <td className="p-3 text-center font-bold border-r border-black">{CAT_LETTERS[catIdx] || (catIdx + 1)}</td>
                                <td colSpan="8" className="p-3 font-bold uppercase tracking-widest text-[#0A5F38] border-r border-black">
                                  <div className="flex justify-between items-center">
                                    <span>{catLabel}</span>
                                    <div className="flex gap-2">
                                       <button 
                                         onClick={() => {
                                           const newLabel = prompt("Enter new section name:", catLabel);
                                           if (newLabel) {
                                             const updated = editingProducts.map(item => 
                                               (item.categoryLabel || 'Section') === catLabel ? { ...item, categoryLabel: newLabel } : item
                                             );
                                             setEditingProducts(updated);
                                           }
                                         }}
                                         className="text-[10px] bg-white/50 px-2 py-1 rounded hover:bg-white transition-colors"
                                       >
                                         Rename
                                       </button>
                                       <button 
                                         onClick={() => {
                                           if (window.confirm(`Delete entire section "${catLabel}"?`)) {
                                             const updated = editingProducts.filter(item => (item.categoryLabel || 'Section') !== catLabel);
                                             setEditingProducts(updated);
                                           }
                                         }}
                                         className="text-[10px] bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                                       >
                                         Delete Section
                                       </button>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-3 border-b border-black"></td>
                              </tr>
                            );

                            // Item Rows
                            catEntries.forEach(({ item, idx }, siNo) => {
                              rows.push(
                                <tr key={'edit-row-' + idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                                  <td className="p-2 text-center text-xs font-medium text-slate-400 border-r border-slate-100">{siNo + 1}</td>
                                  <td className="p-2 border-r border-slate-100">
                                    <input
                                      type="text"
                                      value={item.product_name}
                                      onChange={(e) => handleProductEdit(idx, 'product_name', e.target.value)}
                                      className="w-full bg-transparent border-0 focus:ring-0 text-sm font-medium py-1 px-0"
                                      placeholder="Enter item description..."
                                    />
                                  </td>
                                  <td className="p-2 border-r border-slate-100">
                                    <input
                                      type="number"
                                      value={item.unit_price}
                                      onChange={(e) => handleProductEdit(idx, 'unit_price', e.target.value)}
                                      className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm font-semibold focus:ring-1 focus:ring-[#C5A57D]"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                  <td className="p-2 border-r border-slate-100">
                                    <input
                                      type="number"
                                      value={item.nos}
                                      onChange={(e) => handleProductEdit(idx, 'nos', e.target.value)}
                                      className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                  <td className="p-2 border-r border-slate-100">
                                    <input
                                      type="number"
                                      value={item.length}
                                      onChange={(e) => handleProductEdit(idx, 'length', e.target.value)}
                                      className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                  <td className="p-2 border-r border-slate-100">
                                    <input
                                      type="number"
                                      value={item.breadth}
                                      onChange={(e) => handleProductEdit(idx, 'breadth', e.target.value)}
                                      className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                  <td className="p-2 text-center text-sm font-bold text-slate-700 bg-slate-50 border-r border-slate-100">
                                    {item.quantity}
                                  </td>
                                  <td className="p-2 border-r border-slate-100">
                                    <input
                                      type="text"
                                      value={item.gst_rate === 0 ? '' : item.gst_rate}
                                      onChange={(e) => handleProductEdit(idx, 'gst_rate', e.target.value)}
                                      className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                      placeholder="0"
                                    />
                                  </td>
                                  <td className="p-2 text-right text-sm font-black text-[#0A5F38] bg-slate-50 pr-4 border-r border-slate-100">
                                    ₹{item.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                  </td>
                                  <td className="p-2 text-center">
                                    <button
                                      onClick={() => handleRemoveProduct(idx)}
                                      className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            });

                            // Add Row for this Category
                            rows.push(
                              <tr key={'add-row-' + catLabel} className="bg-white border-b border-black">
                                 <td colSpan="10" className="p-2">
                                    <button
                                      onClick={() => {
                                        const newItem = {
                                          categoryLabel: catLabel,
                                          product_name: '',
                                          unit_price: 1000,
                                          nos: 1,
                                          length: 1,
                                          breadth: 1,
                                          quantity: 1,
                                          gst_rate: 0,
                                          amount: 1000,
                                          total_amount: 1000,
                                          unit_type: 'SFT'
                                        };
                                        setEditingProducts([...editingProducts, newItem]);
                                      }}
                                      className="flex items-center gap-2 text-[10px] font-bold text-[#0A5F38] hover:bg-[#0A5F38] hover:text-white transition-all px-4 py-1 rounded border border-[#0A5F38]"
                                    >
                                      <Plus className="w-3 h-3" />
                                      ADD ITEM TO {catLabel}
                                    </button>
                                 </td>
                              </tr>
                            );
                          });

                          return rows;
                        })()}
                      </tbody>
                      <tfoot className={modalTable.tfoot}>
                        <tr className="border-t-2 border-black">
                           <td colSpan="8" className="p-4 text-right font-bold text-slate-700 uppercase">Grand Total:</td>
                           <td className="p-4 text-right font-black text-[#0A5F38] text-xl italic">
                             ₹{editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                           </td>
                           <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  /* ══ NORMAL RETAIL TABLE EDIT LAYOUT ══ */
                  editingProducts.length > 0 ? (
                    <div className={modalTable.wrap}>
                      <table className={`${modalTable.table} table-fixed`} style={{ minWidth: '1000px' }}>
                        <colgroup>
                          <col style={{ width: '180px' }} />
                          <col style={{ minWidth: '240px' }} />
                          <col style={{ width: '100px' }} />
                          <col style={{ width: '90px' }} />
                          <col style={{ width: '110px' }} />
                          {editingApplyDiscount && <col style={{ width: '80px' }} />}
                          <col style={{ width: '90px' }} />
                          <col style={{ width: '130px' }} />
                          <col style={{ width: '48px' }} />
                        </colgroup>
                        <thead className={modalTable.thead}>
                          <tr>
                            <th className={modalTable.th}>Category</th>
                            <th className={modalTable.th}>Product</th>
                            <th className={modalTable.thCenter}>Units</th>
                            <th className={modalTable.thCenter}>Qty</th>
                            <th className={modalTable.thRight}>Price</th>
                            {editingApplyDiscount && (
                              <th className={modalTable.thCenter}>Disc %</th>
                            )}
                            <th className={modalTable.thCenter}>GST %</th>
                            <th className={modalTable.thRight}>Total</th>
                            <th className={modalTable.thCenter}></th>
                          </tr>
                        </thead>
                        <tbody className={modalTable.tbody}>
                          {editingProducts.map((item, index) => (
                            <tr key={index} className={modalTable.tr}>
                              {/* Category */}
                              <td className={modalTable.td}>
                                  <select
                                    value={item.category_id || ''}
                                    onChange={(e) => handleProductEdit(index, 'category_id', e.target.value)}
                                  className={modalTable.input}
                                  >
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                      <option key={cat.id || cat._id} value={cat.id || cat._id}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                              </td>

                              {/* Product */}
                              <td className={modalTable.td}>
                                  {item.isOthers ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="text"
                                        value={item.product_name || ''}
                                        onChange={(e) => handleProductEdit(index, 'product_name', e.target.value)}
                                      className={`${modalTable.input} flex-1 font-medium`}
                                        placeholder="Enter product name"
                                      />
                                    </div>
                                  ) : (
                                    <select
                                      value={item.product_id || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const product = products.find(p => String(p.id || p._id) === String(val));
                                        if (product) {
                                          handleProductSelect(index, val, product);
                                        } else {
                                          handleProductEdit(index, 'product_id', val);
                                        }
                                      }}
                                      className={`${modalTable.input} font-medium`}
                                      disabled={!item.category_id}
                                    >
                                      <option value="">Select Product</option>
                                      {item.category_id && products
                                        .filter(prod => String(prod.categoryId || prod.category_id || '') === String(item.category_id))
                                        .map(prod => (
                                          <option key={prod.id} value={prod.id}>
                                            {prod.name}
                                          </option>
                                        ))}
                                    </select>
                                  )}
                              </td>

                              {/* Units */}
                              <td className={modalTable.td}>
                                  <select
                                    value={item.unit_type || 'SFT'}
                                    onChange={(e) => handleProductEdit(index, 'unit_type', e.target.value)}
                                  className={`${modalTable.input} px-1`}
                                  >
                                    <option value="SFT">SFT</option>
                                    <option value="RFT">RFT</option>
                                    <option value="PCS">PCS</option>
                                    <option value="KG">KG</option>
                                    <option value="LTR">LTR</option>
                                    <option value="MTR">MTR</option>
                                    <option value="LUMPSUM">LUMPSUM</option>
                                    <option value="NOS">NOS</option>
                                    <option value="SQ.M">SQ.M</option>
                                  </select>
                              </td>

                              {/* Quantity */}
                              <td className={modalTable.td}>
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleProductEdit(index, 'quantity', e.target.value)}
                                  className={`${modalTable.input} text-center`}
                                  />
                              </td>

                              {/* Price */}
                              <td className={modalTable.td}>
                                  <input
                                    type="number"
                                    value={item.unit_price}
                                    onChange={(e) => handleProductEdit(index, 'unit_price', e.target.value)}
                                  className={`${modalTable.input} text-right font-semibold`}
                                  />
                              </td>

                              {/* Discount */}
                              {editingApplyDiscount && (
                                <td className={modalTable.td}>
                                    <input
                                      type="number"
                                      value={item.discount}
                                      onChange={(e) => handleProductEdit(index, 'discount', e.target.value)}
                                    className={`${modalTable.input} text-center`}
                                    />
                                </td>
                              )}

                              {/* GST % */}
                              <td className={modalTable.td}>
                                  <select
                                    value={item.gst_rate}
                                    onChange={(e) => handleProductEdit(index, 'gst_rate', e.target.value)}
                                  className={modalTable.input}
                                  >
                                    <option value="0">0%</option>
                                    <option value="5">5%</option>
                                    <option value="12">12%</option>
                                    <option value="18">18%</option>
                                    <option value="28">28%</option>
                                  </select>
                              </td>

                              {/* Total */}
                              <td className={`${modalTable.tdRight} font-bold text-[#0A5F38]`}>
                                  ₹{(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                              </td>

                              {/* Actions */}
                              <td className={modalTable.tdCenter}>
                                  <button
                                    onClick={() => handleRemoveProduct(index)}
                                    className="text-slate-300 hover:text-red-500 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-slate-200">
                      <p className="text-slate-400 font-medium">No products added yet</p>
                    </div>
                  )
                )}

                <button
                  onClick={handleAddProduct}
                  className="w-full mt-4 px-4 py-4 border-2 border-dashed border-[#C5A57D] rounded-2xl text-[#C5A57D] hover:bg-[#C5A57D] hover:text-white transition-all font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  {isWoodWorkMode ? 'Add New Section' : 'Add New Product'}
                </button>
              </div>
          </AppModal>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingOrder && (
          <AppModal
            onClose={() => { setShowDeleteModal(false); setDeletingOrder(null); }}
            title="Delete Closed Order"
            size="sm"
            layer="top"
            footer={
              <>
                <button
                  onClick={() => { setShowDeleteModal(false); setDeletingOrder(null); }}
                  className="px-4 py-2 border border-[#C5A57D] text-[#C5A57D] rounded-lg hover:bg-[#C5A57D] hover:bg-opacity-10 transition-colors bg-white font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteClosedOrder}
                  className="px-4 py-2 bg-[#B22222] text-white rounded-lg hover:bg-[#8f1b1b] transition-colors font-medium shadow-sm"
                >
                  Delete
                </button>
              </>
            }
          >
              <div className="text-center">
                <p className="text-slate-900 font-medium mb-2">
                  Are you sure you want to delete closed order <span className="font-bold">{deletingOrder.closed_order_number}</span>?
                </p>
                <p className="text-slate-600">
                  This action cannot be undone.
                </p>
              </div>
          </AppModal>
        )}
      </div>
    </div >

  );
};

export default AdminClosedOrders;





